import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, Upload, Plus, Clock, LayoutGrid, MapPin, RotateCcw, MoreVertical, Loader2, Trash2, ArrowRightLeft, Mail, MessageSquare, Palette, SlidersHorizontal, Users, ChevronDown, Eye, Lock, Share2, Globe, Pencil, X, ArrowUp, ArrowDown, ArrowUpDown, Ban, GripVertical, ArrowLeft, ArrowRight, Layers, KanbanSquare } from "lucide-react";
import toast from "react-hot-toast";
import {
  getProspects,
  getProspectLists,
  createProspectList,
  updateProspectList,
  deleteProspectList,
  mergeProspectList,
  renameProspectGroup,
  getCustomColumns,
  createCustomColumn,
  updateCustomColumn,
  deleteCustomColumn,
  updateProspect,
  getViewConfig,
  saveViewConfig,
  bulkProspectAction,
  getProspectAppraisalPrefill,
  getDatabaseShares,
  markSharedDatabaseOpened,
} from "../../../api/database";
import ContactLink from "../../contacts/components/ContactLink";
import BulkBuyerPreferencesModal from "../components/BulkBuyerPreferencesModal";
import ProspectDrawer from "../components/ProspectDrawer";
import ImportWizard from "../components/ImportWizard";
import ProspectMap from "../components/ProspectMap";
import useGridWindow from "../hooks/useGridWindow";
import AddRecordModal from "../components/AddRecordModal";
import MoveToListModal from "../components/MoveToListModal";
import MoveToPipelineModal from "../components/MoveToPipelineModal";
import ListFormModal from "../components/ListFormModal";
import MergeListModal from "../components/MergeListModal";
import DeleteImportModal from "../components/DeleteImportModal";
import ShareDatabaseModal from "../components/ShareDatabaseModal";
import TransferDatabaseModal from "../components/TransferDatabaseModal";
import ColourPicker, { COLOURS, colourOf } from "../components/ColourPicker";
import { compareBy } from "../sortRows";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";
import { applySavedOrder, moveInOrder } from "../tabOrder";
import { emptyCategoryTitles, resolveListChoice } from "../emptyCategories";
import { MarketStatusBadge, MARKET_FILTERS } from "../components/MarketStatus";

const TEAL = "#55c5d0";

const TEMP_STYLES = {
  cold: "bg-[#eaf4fb] text-[#2f9bd6]",
  warm: "bg-[#fdeede] text-[#e8923a]",
  hot: "bg-[#e9f7ee] text-[#34b56a]",
};

// Do not contact. Paul Jimoulis, Monday 12931774512 (2026-08-31): "make sure that
// its visible so agents dont call them". The flag lives on the mirrored CRM
// contact, so the same tick shows here, on the Contacts page, and as an exclusion
// from every campaign audience.
const isUnsubbed = (r) => Boolean(r.unsubscribed);
// Which of the two people on the row it was: a joint row can have one owner
// unsubscribed and the other perfectly happy to be called.
const unsubbedNames = (r) => r.unsubscribed_names || [];
// Solid rose left edge. It OUTRANKS the call-back green and the agent's own
// colour code, because those are working preferences and this one is a
// compliance stop - it has to be the signal you cannot miss.
const UNSUB_EDGE = "#e5484d";

// A row the agent has already worked: they spoke to the contact, they weren't
// selling yet, and a call-back was booked. It turns GREEN and the API drops it to
// the bottom of the list in call-back order (Christian Gravias, 2026-08-04).
const hasCallback = (r) => Boolean(r.next_call_at);

// The worked-row green. Christian asked for it to be PERMANENT green rather than
// something you only see under the cursor (2026-08-05): the first tint (#eaf7ef)
// was so pale against the white grid that the row only looked green on hover.
// These read as green at rest, and hover only deepens them a little. Written as
// whole literal class strings so Tailwind's scanner still picks them up.
const GREEN_ROW = "bg-[#c3ebd3] hover:bg-[#aee2c1]";
// A green row that is also ticked for a bulk action: still green, just deeper.
const GREEN_ROW_SELECTED = "bg-[#aee2c1] hover:bg-[#aee2c1]";
// Solid left edge, so a worked row is unmistakable even on a small screen.
const GREEN_EDGE = "#34b56a";

// A row already marked as an appraisal. It used to vanish from the Database the
// moment it was converted; Christian asked for it to stay visible and be flagged
// with a different-coloured row instead (Monday 13198140343). Violet, so it can't
// be mistaken for the call-back green or the do-not-contact rose.
const isAppraisal = (r) => r.status === "converted";
const APPRAISAL_ROW = "bg-[#e6defb] hover:bg-[#d9cdf8]";
const APPRAISAL_ROW_SELECTED = "bg-[#d9cdf8] hover:bg-[#d9cdf8]";
const APPRAISAL_EDGE = "#7c5cdb";

// Today as YYYY-MM-DD in the browser's timezone. next_call_at is a plain date,
// so string comparison is exact and avoids UTC drift from toISOString().
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// A booked call-back that has come around: still green, but the date reads amber
// so the agent can see it needs ringing today rather than sitting in the future.
const callbackDue = (r) => hasCallback(r) && r.next_call_at <= todayISO();

// Every mobile / email captured for the PRIMARY person on a row. The second
// owner has their own columns (second_mobile / second_email); anything past the
// two people stays in additional_* so it is still visible.
const allMobiles = (r) => [r.mobile, ...(r.additional_mobiles || [])].filter(Boolean);
const allEmails = (r) => [r.email, ...(r.additional_emails || [])].filter(Boolean);

// Standard column definitions per view. `get` reads a value off the record.
const OWNER_COLUMNS = [
  { key: "address", label: "Address", get: (r) => r.owner_profile?.address, bold: true },
  { key: "suburb", label: "Suburb", get: (r) => r.owner_profile?.suburb },
  { key: "property_type", label: "Type", get: (r) => r.owner_profile?.property_type },
  { key: "beds", label: "Beds", get: (r) => r.owner_profile?.beds },
  { key: "baths", label: "Baths", get: (r) => r.owner_profile?.baths },
  { key: "land_sqm", label: "Land m²", get: (r) => r.owner_profile?.land_sqm },
  // Sheets record the last sale as a price, a date, or both — show whatever the
  // upload carried so a date-only column doesn't read as empty.
  {
    key: "last_sold_price",
    label: "Last sold",
    get: (r) =>
      [money(r.owner_profile?.last_sold_price), auDate(r.owner_profile?.last_sold_date)]
        .filter(Boolean)
        .join(" · "),
  },
  { key: "name", label: "Owner", get: (r) => r.name, person: 0 },
  { key: "mobile", label: "Mobile", get: (r) => allMobiles(r).join(", ") },
  { key: "email", label: "Email", get: (r) => allEmails(r).join(", ") },
  // A row for a jointly-owned property carries two people. The second owner gets
  // their own name/mobile/email so the row splits into two contacts under the one
  // property profile (Christian Gravias, 2026-08-03).
  { key: "second_name", label: "2nd Owner", get: (r) => r.second_name, person: 1 },
  { key: "second_mobile", label: "2nd Mobile", get: (r) => r.second_mobile },
  { key: "second_email", label: "2nd Email", get: (r) => r.second_email },
  { key: "lead_temp", label: "Temp", temp: true, get: (r) => r.lead_temp },
  // The agent's own answer, plus Homer's For sale / Withdrawn reading beside it
  // (Christian Gravias, Monday 13198140343, 17 Oct 2026).
  { key: "timeframe", label: "Looking to sell", market: true, get: (r) => r.timeframe },
  { key: "next_call_at", label: "Next call", due: true, get: (r) => r.next_call_at },
  { key: "last_note", label: "Last note", note: true, get: (r) => r.last_note },
];

const BUYER_COLUMNS = [
  { key: "name", label: "Name", get: (r) => r.name, bold: true, person: 0 },
  { key: "buyer_type", label: "Buyer Type", get: (r) => r.buyer_profile?.buyer_type },
  { key: "budget", label: "Budget", money: true, get: (r) => r.buyer_profile?.budget },
  { key: "target_suburbs", label: "Target suburbs", get: (r) => (r.buyer_profile?.target_suburbs || []).join(", ") },
  { key: "wanted_type", label: "Property Type", get: (r) => r.buyer_profile?.wanted_type },
  { key: "beds_min", label: "Beds min", get: (r) => r.buyer_profile?.beds_min },
  { key: "mobile", label: "Mobile", get: (r) => allMobiles(r).join(", ") },
  { key: "email", label: "Email", get: (r) => allEmails(r).join(", ") },
  // Couples buy together: the second person on the row keeps their own details.
  { key: "second_name", label: "2nd Buyer", get: (r) => r.second_name, person: 1 },
  { key: "second_mobile", label: "2nd Mobile", get: (r) => r.second_mobile },
  { key: "second_email", label: "2nd Email", get: (r) => r.second_email },
  { key: "lead_temp", label: "Temp", temp: true, get: (r) => r.lead_temp },
  { key: "timeframe", label: "Looking to buy", get: (r) => r.timeframe },
  { key: "agent_name", label: "Agent", get: (r) => r.agent_name },
  { key: "next_call_at", label: "Next call", due: true, get: (r) => r.next_call_at },
  { key: "last_note", label: "Last note", note: true, get: (r) => r.last_note },
];

const money = (v) => (v ? "$" + Number(v).toLocaleString() : "");

// A plain date field (2013-01-25) as 25/01/2013. Formatted off the string so no
// timezone shift can move it a day.
const auDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

// Turn a raw field key/header into a readable column label: "last_contact" -> "Last Contact".
const prettify = (k) =>
  String(k || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// Short date for the "Last note" preview.
const noteDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

// A date/time an agent typed (or a spreadsheet carried) at the START of a note:
// "05/10/2026 12:15 PM - Spoke to...", "[5 Oct 2026, 10:12 am] ...". In a one-line
// cell that stamp was all anyone could see (Simon Abbott, CRM-2026-00087), so the
// preview leads with what the note says and shows the stamp after it instead.
const LEADING_STAMP =
  /^\s*[[(]?\s*(?:\d{1,4}[/.-]\d{1,2}[/.-]\d{2,4}|\d{1,2}(?:st|nd|rd|th)?\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?,?\s+\d{2,4})(?:\s*(?:,|T|@|at)?\s*\d{1,2}[:.]\d{2}(?::\d{2})?\s*(?:[ap]\.?\s?m\.?)?)?\s*[\])]?\s*[-–—:|,]?\s*/i;

export const splitNoteStamp = (body) => {
  const text = String(body || "").replace(/\s+/g, " ").trim();
  const m = LEADING_STAMP.exec(text);
  if (!m) return { text, stamp: "" };
  const rest = text.slice(m[0].length).trim();
  // A note that is ONLY a date keeps it, rather than showing nothing.
  if (!rest) return { text, stamp: "" };
  return { text: rest, stamp: m[0].replace(/[\s[\]()\-–—:|,]+$/, "").replace(/^[\s[(]+/, "") };
};

// Wide enough to read the gist of a note in the grid. The column widths are taken
// from the first screenful of rows, so without a fixed width a list whose top rows
// had short (or no) notes left this column too narrow for everyone below them.
const NOTE_CELL_W = 340;

// The "Last note" cell: one line in the grid (rows must stay one height for the
// windowed scrolling), the whole note in a card on hover.
export function NotePreview({ note }) {
  const ref = useRef(null);
  const hideTimer = useRef(null);
  const [pos, setPos] = useState(null);
  const { text, stamp } = splitNoteStamp(note.body);
  const when = stamp || noteDate(note.created_at);

  const show = () => {
    clearTimeout(hideTimer.current);
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cardW = 380;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - cardW - 8));
    // Open upwards when the row is near the bottom of the window.
    const below = rect.bottom + 6;
    const up = window.innerHeight - rect.bottom < 240;
    setPos(up ? { left, bottom: window.innerHeight - rect.top + 6 } : { left, top: below });
  };
  const hide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setPos(null), 120);
  };
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={hide}
        className="inline-block align-middle truncate text-gray-700"
        style={{ width: NOTE_CELL_W, maxWidth: NOTE_CELL_W }}
      >
        {text}
        {(when || note.author_name) && (
          <span className="text-gray-400">
            {when ? ` · ${when}` : ""}
            {note.author_name ? ` · ${note.author_name}` : ""}
          </span>
        )}
      </span>
      {pos &&
        createPortal(
          <div
            onMouseEnter={() => clearTimeout(hideTimer.current)}
            onMouseLeave={hide}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[9999] w-[380px] bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-left"
            style={pos}
          >
            <p className="text-[10.5px] uppercase tracking-wide text-gray-400 font-extrabold mb-1">
              Last note
              {note.created_at ? ` · ${formatMelbourneDateTime(note.created_at)}` : ""}
              {note.author_name ? ` · ${note.author_name}` : ""}
            </p>
            <p className="text-[12.8px] text-gray-700 whitespace-pre-wrap break-words max-h-[220px] overflow-y-auto">
              {note.body}
            </p>
          </div>,
          document.body
        )}
    </>
  );
}

// Category and list names are compared loosely: a CSV import can spell the same
// suburb differently, and one category is one row either way. Module level so it
// can be used inside the render-time useMemos as well as the handlers.
const sameName = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

// Pull a readable message out of an axios error (handles DRF {detail} or {field:[...]}).
function apiError(e, fallback) {
  const d = e?.response?.data;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (d.detail) return typeof d.detail === "string" ? d.detail : fallback;
  const first = Object.values(d)[0];
  if (Array.isArray(first)) return first[0];
  if (typeof first === "string") return first;
  return fallback;
}

// `embedded`: shown inside the Prospects page (Pipeline / Database toggle,
// Monday 12988442847), which already carries the page title and padding, so the
// heading drops to a sub heading and the outer padding goes. /database itself is
// unchanged.
export default function DatabasePage({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [view, setView] = useState("owners");
  const [mode, setMode] = useState("grid");
  const [records, setRecords] = useState([]);
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState("all");
  const [customCols, setCustomCols] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [order, setOrder] = useState([]);
  const [labels, setLabels] = useState({}); // per-column display-label overrides
  // Categories the agent made that hold no lists yet. A category is normally
  // just the heading its lists sit under, so an empty one is kept on the view
  // config instead (Christian Gravias, 2026-08-22: "Not every category needs a
  // sub category. Please make it optional that I can just do Category name").
  const [emptyGroups, setEmptyGroups] = useState([]);
  // The order the agent dragged the two tab strips into (Monday 12963781026):
  // category headings along the top, ProspectList ids for the sheets along the
  // bottom. Both are saved on the view config, so the order survives a reload
  // and follows the agent between machines.
  const [groupOrder, setGroupOrder] = useState([]);
  const [listOrder, setListOrder] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [temp, setTemp] = useState("");
  const [colour, setColour] = useState("");
  const [colourRow, setColourRow] = useState(null); // record id whose palette is open
  const [bulkColourOpen, setBulkColourOpen] = useState(false);
  // '' any / '1' only unsubscribed / '0' hide them.
  const [unsubFilter, setUnsubFilter] = useState("");
  // Homer market status: '' any / 'any' for sale or withdrawn / 'for_sale' / 'withdrawn'.
  const [market, setMarket] = useState("");
  // The open "Move to Pipeline" picker: { ids, defaultPlacement, bulk }.
  const [pipelineMove, setPipelineMove] = useState(null);
  // Bulk "Set buying preferences" over the ticked rows (Monday DOMA 12840979019).
  const [bulkPrefsOpen, setBulkPrefsOpen] = useState(false);
  // Which of the bulk Email / Text buttons is mid-flight ('email' | 'sms' | null).
  const [bulkSending, setBulkSending] = useState(null);
  const [suburb, setSuburb] = useState("");
  const [beds, setBeds] = useState("");
  const [callbacksDue, setCallbacksDue] = useState(false);
  const [showConverted, setShowConverted] = useState(false);

  const [selected, setSelected] = useState(new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showMoveList, setShowMoveList] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [drawerRefresh, setDrawerRefresh] = useState(0);
  // Database sharing (Christian Gravias, Monday 12817267594): agents work in
  // teams, so an agent can hand a colleague access to their Database and switch
  // between the databases shared with them. `viewingAgent` null means "mine";
  // otherwise every load is scoped to that agent's records and the owner-only
  // controls (upload, lists, columns) come off the page.
  const [viewingAgent, setViewingAgent] = useState(null); // { id, name, canEdit }
  const [sharedWithMe, setSharedWithMe] = useState([]);
  // "principal" (super admin) or "tech" (dev) when the switcher rows come from
  // the role rather than from an invitation, else null. Only the wording
  // differs; the access is the same read-only view either way.
  const [reviewReason, setReviewReason] = useState(null);
  // Who "mine" is. Used by the bulk-delete confirm to name the agents whose
  // rows are about to go, which an edit-level share makes possible.
  const [me, setMe] = useState(null);
  const [showShare, setShowShare] = useState(false);
  // Super admin moving a leaving agent's Database to another agent
  // (Christian Gravias, Monday 13202305277).
  const [showTransfer, setShowTransfer] = useState(false);
  // Sharing ONE group or ONE tab rather than the whole Database (Christian
  // Gravias, 2026-08-18): "we might have 3 agents who are only invited to this
  // one group, or public groups that the whole of Collings could be invited
  // to... right click on a tab and just share people & remove from groups".
  // `scopedShares` is what I have already shared that way, so the tabs can be
  // badged; `shareTarget` is the group/tab whose share popover is open.
  const [scopedShares, setScopedShares] = useState([]);
  const [shareTarget, setShareTarget] = useState(null);
  const [tabMenu, setTabMenu] = useState(null); // right-click menu on a tab/group
  // The new-list / new-category / rename-and-move dialog:
  // { mode: "list" | "category" | "edit", list, defaultGroup }
  const [listForm, setListForm] = useState(null);
  const [mergeFrom, setMergeFrom] = useState(null); // tab being merged into another
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [menuCol, setMenuCol] = useState(null); // key of the open column menu
  // Which column the grid is sorted by, or null for the default calling order
  // (hottest first, Hot > Warm > Cold; within each, call-backs after the rows
  // still to be called, soonest first - see the API's order_by).
  const [sortBy, setSortBy] = useState(null); // { key, dir: "asc" | "desc" }
  // Only the newest records request may write to the grid: a big unscoped call
  // can land AFTER the small scoped one that replaced it and overwrite it
  // (Simon Abbott, 26 Aug 2026).
  const recordsSeq = useRef(0);
  const [editing, setEditing] = useState(null); // { recordId, colKey } being edited inline
  const dragKey = useRef(null);

  const baseColumns = view === "owners" ? OWNER_COLUMNS : BUYER_COLUMNS;

  // The agent id every request is scoped to, or undefined for your own Database.
  const agentScope = viewingAgent?.id;
  const isShared = Boolean(agentScope);
  // Working a colleague's rows takes an edit-level share. Read-only shares still
  // show everything; they just can't change it.
  const canWrite = !isShared || Boolean(viewingAgent?.canEdit);
  // Uploads, lists and columns are the shape of the database itself and stay
  // with its owner, so they are hidden on a shared database however it was
  // shared. Mirrors what the API allows (prospecting.services.sharing).
  const canManage = !isShared;
  // The switcher entry for the database currently open: it says whether the
  // whole Database was shared or only a suburb or two, which the page repeats
  // so nobody wonders where the rest of the rows went.
  const currentShare = useMemo(
    () => sharedWithMe.find((s) => String(s.owner) === String(agentScope)),
    [sharedWithMe, agentScope]
  );
  const partialTargets =
    currentShare && !currentShare.full ? currentShare.targets || [] : [];
  // The agency principal and a dev/QA account read every database by role
  // rather than by invitation (prospecting.services.sharing.has_review_access).
  // Those switcher rows are flagged so the page never claims a colleague shared
  // anything, and `reviewReason` decides which of the two it says.
  const reviewAccess = useMemo(
    () => sharedWithMe.some((s) => s.via_review),
    [sharedWithMe]
  );
  const viaReview = Boolean(currentShare?.via_review);
  const isPrincipalView = reviewReason === "principal";
  // Move to Pipeline is the one change the principal can make on an agent's
  // read-only Database: the lead goes on THAT agent's board (Michael Piccoli,
  // CRM-2026-00121, 23 Oct 2026: "There's no button in the databasing
  // section"). Mirrors prospecting.services.sharing.record_pipeline_scope.
  const canMoveToPipeline = canWrite || (isShared && viaReview && isPrincipalView);
  // Tick boxes only where the bulk bar has something to offer.
  const canSelect = canWrite || (canMoveToPipeline && view === "owners");

  // Merge standard + custom columns, apply saved order, drop hidden ones.
  const allColumns = useMemo(() => {
    const custom = customCols.map((c) => ({
      key: c.key,
      label: c.label,
      money: c.type === "money",
      custom: true,
      columnId: c.id,
      type: c.type,
      get: (r) => r.custom_fields?.[c.key],
    }));
    // Data-driven safety net: surface any custom_fields key that carries data
    // but has no column yet (the backend also persists these on load). This
    // guarantees uploaded data is never hidden just because a column is missing.
    const known = new Set([...baseColumns, ...custom].map((c) => c.key));
    const orphanKeys = [];
    for (const r of records) {
      const cf = r.custom_fields || {};
      for (const k of Object.keys(cf)) {
        const v = cf[k];
        if (v === null || v === undefined || v === "") continue;
        if (!known.has(k) && !orphanKeys.includes(k)) orphanKeys.push(k);
      }
    }
    const orphans = orphanKeys.map((k) => ({
      key: k,
      label: prettify(k),
      custom: true,
      get: (r) => r.custom_fields?.[k],
    }));
    // Apply per-agent label overrides (lets built-in columns be renamed).
    let cols = [...baseColumns, ...custom, ...orphans].map((c) => ({
      ...c,
      label: labels[c.key] || c.label,
    }));
    if (order.length) {
      cols = [...cols].sort((a, b) => {
        const ia = order.indexOf(a.key);
        const ib = order.indexOf(b.key);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
    return cols;
  }, [baseColumns, customCols, order, labels, records]);

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => !hidden.includes(c.key)),
    [allColumns, hidden]
  );

  // The rows as they are drawn. Untouched until a column's arrow is clicked, so
  // the default remains the calling order the API hands back.
  const sortedRecords = useMemo(() => {
    if (!sortBy) return records;
    const col = allColumns.find((c) => c.key === sortBy.key);
    if (!col) return records;
    return [...records].sort(compareBy(col, sortBy.dir));
  }, [records, allColumns, sortBy]);

  // Only the rows on screen are drawn. Every row is still in `records`, so
  // sorting, Select all, the filters and the counts all work on the whole
  // database exactly as before - see ../hooks/useGridWindow for why.
  const scrollRef = useRef(null);
  const tableRef = useRef(null);
  const columnsKey = useMemo(
    () => visibleColumns.map((c) => c.key).join("|"),
    [visibleColumns]
  );
  const grid = useGridWindow({
    rowCount: sortedRecords.length,
    scrollRef,
    tableRef,
    resetKey: `${view}:${agentScope || "mine"}:${activeList}:${showConverted}`,
    columnsKey,
  });
  const drawnRecords = grid.windowed
    ? sortedRecords.slice(grid.first, grid.last)
    : sortedRecords;
  // One <td> per header cell: tick box, colour, the columns.
  const gridColCount = visibleColumns.length + 2;

  // A-Z, then Z-A, then back to the default order.
  const toggleSort = (key) =>
    setSortBy((current) => {
      if (!current || current.key !== key) return { key, dir: "asc" };
      return current.dir === "asc" ? { key, dir: "desc" } : null;
    });

  const loadConfig = useCallback(async (v, scope) => {
    const [colRes, cfgRes] = await Promise.all([
      getCustomColumns(v, scope).catch(() => ({ data: [] })),
      getViewConfig(v, scope).catch(() => ({ data: {} })),
    ]);
    setCustomCols(colRes.data || []);
    setHidden(cfgRes.data?.hidden_columns || []);
    setOrder(cfgRes.data?.column_order || []);
    setLabels(cfgRes.data?.column_labels || {});
    // Empty categories are the owner's own scaffolding and hold nothing to
    // read, so a shared Database doesn't list someone else's headings.
    setEmptyGroups(scope ? [] : cfgRes.data?.empty_groups || []);
    // A shared Database is drawn in ITS OWNER's tab order, the same way it is
    // drawn with their column order: the API hands back the owner's config, and
    // dragging is off unless it's your own Database.
    setGroupOrder(cfgRes.data?.group_order || []);
    setListOrder(cfgRes.data?.list_order || []);
  }, []);

  const loadLists = useCallback(async (v, scope) => {
    const res = await getProspectLists(v, scope).catch(() => ({ data: [] }));
    setLists(res.data || []);
  }, []);

  const loadRecords = useCallback(async () => {
    const seq = ++recordsSeq.current;
    const isCurrent = () => recordsSeq.current === seq;
    setLoading(true);
    try {
      const params = {
        view,
        // Appraisal rows stay in the main view, flagged. The toggle narrows the
        // grid to only those rows.
        status: showConverted ? "converted" : "active,converted",
      };
      if (activeList !== "all") params.list = activeList;
      if (search) params.q = search;
      if (temp) params.temp = temp;
      if (colour) params.colour = colour;
      if (unsubFilter) params.unsubscribed = unsubFilter;
      if (market && view === "owners") params.market = market;
      if (suburb) params.suburb = suburb;
      if (beds) params.beds = beds;
      if (callbacksDue) params.callbacks_due = 1;
      if (agentScope) params.agent = agentScope;
      const res = await getProspects(params);
      if (!isCurrent()) return; // a newer request has already answered
      setRecords(res.data || []);
    } catch (e) {
      if (!isCurrent()) return;
      // Don't let a transient failure crash the whole page — show an empty grid.
      setRecords([]);
      toast.error(apiError(e, "Couldn't load records — try again"));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [view, activeList, search, temp, colour, unsubFilter, market, suburb, beds, callbacksDue, showConverted, agentScope]);

  useEffect(() => {
    loadConfig(view, agentScope);
    loadLists(view, agentScope);
    // Owners and Buyers have different columns, and so does a colleague's
    // database, so a sort chosen over there doesn't follow you across.
    setSortBy(null);
  }, [view, agentScope, loadConfig, loadLists]);

  useEffect(() => {
    const t = setTimeout(loadRecords, 200);
    return () => clearTimeout(t);
  }, [loadRecords]);

  // Which colleagues' databases can be opened, for the switcher. Loaded once;
  // the Share modal hands back a fresh payload whenever it changes something.
  const applyShares = useCallback((payload) => {
    if (payload?.shared_with_me) {
      setSharedWithMe(payload.shared_with_me);
      setScopedShares(payload.scoped_shares || []);
      setMe(payload.me || null);
      setReviewReason(payload.review_reason || null);
      return;
    }
    // A per-group payload only describes that one group, so it can't refresh
    // the switcher or the badges - go and get the whole picture instead.
    getDatabaseShares()
      .then((res) => {
        setSharedWithMe(res.data?.shared_with_me || []);
        setScopedShares(res.data?.scoped_shares || []);
        setMe(res.data?.me || null);
        setReviewReason(res.data?.review_reason || null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getDatabaseShares()
      .then((res) => applyShares(res.data))
      .catch(() => {}); // sharing is additive — never block the grid on it
  }, [applyShares]);

  // Who I have already given this group / this tab to, keyed for the badges.
  // Only ever about MY database: a colleague's shares aren't mine to show.
  const groupShareCounts = useMemo(() => {
    const map = new Map();
    scopedShares
      .filter((s) => s.scope === "group" && (!s.view || s.view === view))
      .forEach((s) => {
        const key = (s.group_name || "").trim();
        const at = map.get(key) || { agents: 0, everyone: false };
        if (s.everyone) at.everyone = true;
        else at.agents += 1;
        map.set(key, at);
      });
    return map;
  }, [scopedShares, view]);

  const listShareCounts = useMemo(() => {
    const map = new Map();
    scopedShares
      .filter((s) => s.scope === "list" && s.prospect_list)
      .forEach((s) => {
        const at = map.get(s.prospect_list) || { agents: 0, everyone: false };
        if (s.everyone) at.everyone = true;
        else at.agents += 1;
        map.set(s.prospect_list, at);
      });
    return map;
  }, [scopedShares]);

  // Open a colleague's Database (or pass null to come back to your own). Clears
  // the view-specific state first: the previous database's rows, tabs and custom
  // columns must not linger under the new one while it loads.
  const openDatabase = useCallback((agentId, name, canEdit) => {
    setViewingAgent(agentId ? { id: agentId, name, canEdit } : null);
    setActiveList("all");
    setSelected(new Set());
    setShowConverted(false);
    setSuburb("");
    setSearch("");
    setDrawerId(null);
    setRecords([]);
    setLists([]);
    setCustomCols([]);
    setLoading(true);
    setSwitcherOpen(false);
    if (agentId) markSharedDatabaseOpened(agentId).catch(() => {});
  }, []);


  // A share email links straight to ?agent=<id>, so the agent lands on the
  // database they were just given rather than having to find the switcher.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || !sharedWithMe.length) return;
    const wanted = new URLSearchParams(window.location.search).get("agent");
    if (!wanted) return;
    const match = sharedWithMe.find((s) => String(s.owner) === String(wanted));
    deepLinked.current = true;
    if (match) {
      openDatabase(
        match.owner,
        match.owner_detail?.name || match.owner_detail?.email,
        match.can_edit
      );
    } else {
      toast.error("That database hasn't been shared with you.");
    }
  }, [sharedWithMe, openDatabase]);

  // A contact profile lists the Database properties that person owns and links
  // each one back here as ?record=<id>, so "Open" lands on the row itself rather
  // than on an unfiltered grid the agent then has to search (CRM-2026-00031).
  const deepLinkedRecord = useRef(false);
  useEffect(() => {
    if (deepLinkedRecord.current) return;
    const wanted = new URLSearchParams(window.location.search).get("record");
    if (!wanted) return;
    deepLinkedRecord.current = true;
    setDrawerId(Number(wanted));
  }, []);

  // Close the database switcher when clicking elsewhere.
  useEffect(() => {
    if (!switcherOpen) return;
    const close = () => setSwitcherOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [switcherOpen]);

  // Close the right-click tab menu the way every other menu here closes.
  useEffect(() => {
    if (!tabMenu) return;
    const close = () => setTabMenu(null);
    document.addEventListener("click", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [tabMenu]);

  // Close any open column menu when clicking elsewhere.
  useEffect(() => {
    if (!menuCol) return;
    const close = () => setMenuCol(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuCol]);

  const switchView = (v) => {
    if (v === view) return;
    setView(v);
    setActiveList("all");
    setSelected(new Set());
    setShowConverted(false);
    setSuburb("");
    // Clear stale view-specific state so the previous view's rows, tabs and
    // custom columns don't linger under the new view while it loads.
    setRecords([]);
    setLists([]);
    setCustomCols([]);
    setLoading(true);
  };

  const suburbs = useMemo(
    () => [...new Set(records.map((r) => r.owner_profile?.suburb).filter(Boolean))].sort(),
    [records]
  );

  // How many of these rows are already worked (green, sitting at the bottom).
  const callbackCount = useMemo(() => records.filter(hasCallback).length, [records]);

  // Every ticked row already flagged? Then the bulk button is the undo rather
  // than a second no-op flagging pass.
  const selectionAllUnsubbed = useMemo(() => {
    const picked = records.filter((r) => selected.has(r.id));
    return picked.length > 0 && picked.every(isUnsubbed);
  }, [records, selected]);

  // ---- selection / bulk ----
  const toggleRow = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === records.length) setSelected(new Set());
    else setSelected(new Set(records.map((r) => r.id)));
  };
  const runBulk = async (action) => {
    const ids = [...selected];
    try {
      await bulkProspectAction({ action, ids });
      toast.success("Done");
      setSelected(new Set());
      loadRecords();
    } catch (e) {
      toast.error(apiError(e, "Bulk action failed"));
    }
  };

  // Email / Text the ticked rows. The send engine only ever resolves a saved
  // audience, so the server turns the selection into one and hands back its id;
  // we then open the composer already pointed at those people. Christian
  // Gravias, Monday 12908273441 (2026-08-27): "Clicked the Email button but it
  // says done. It doesnt take me to the page to email them. Please also add an
  // SMS button so the agents can SMS them."
  const runBulkSend = async (channel) => {
    const ids = [...selected];
    if (!ids.length) return;
    const verb = channel === "sms" ? "text" : "email";
    setBulkSending(channel);
    try {
      const res = await bulkProspectAction({
        action: channel,
        ids,
        list_name: lists.find((l) => String(l.id) === String(activeList))?.name || "",
      });
      const { segment_id: segmentId, segment_ids: segmentIds, reachable, contacts } = res.data || {};
      if (!segmentId) {
        toast.error(res.data?.detail || `Couldn't build that ${verb} list`);
        return;
      }
      if (!reachable) {
        // The list exists but consent/missing details leave nobody to send to,
        // so say that here rather than dropping the agent on an empty composer.
        toast.error(
          `None of those ${contacts} contact${contacts === 1 ? "" : "s"} can be ${
            channel === "sms" ? "texted" : "emailed"
          } - no ${channel === "sms" ? "mobile" : "email address"} or they've opted out.`
        );
        return;
      }
      toast.success(res.data.detail);
      setSelected(new Set());
      // Ticking a whole list sends from that list's own audience, so the
      // hand-off can carry more than one id (Monday 12975288716).
      const audience = (segmentIds?.length ? segmentIds : [segmentId]).join(",");
      navigate(`/communications/campaigns/new?segment=${audience}&channel=${channel}`);
    } catch (e) {
      toast.error(apiError(e, `Couldn't start that ${verb}`));
    } finally {
      setBulkSending(null);
    }
  };

  // Colour-code one row. Saved straight away and shown immediately, so working
  // down a calling list stays one click per row (Christian Gravias, 2026-08-05).
  const setRowColour = async (record, next) => {
    setColourRow(null);
    if ((record.colour || "") === next) return;
    setRecords((rows) => rows.map((r) => (r.id === record.id ? { ...r, colour: next } : r)));
    try {
      await updateProspect(record.id, { colour: next });
    } catch (e) {
      toast.error(apiError(e, "Couldn't save that colour"));
      loadRecords();
    }
  };

  const runBulkUnsubscribe = async (on) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(
      on
        ? `Mark ${ids.length} row${ids.length === 1 ? "" : "s"} Do not contact? Everyone on them is excluded from every email and SMS from now on.`
        : `Allow contact with ${ids.length} row${ids.length === 1 ? "" : "s"} again?`
    )) return;
    try {
      const res = await bulkProspectAction({ action: "unsubscribe", ids, unsubscribed: on });
      toast.success(res.data?.detail || "Done");
      setSelected(new Set());
      loadRecords();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update those contacts"));
    }
  };

  const runBulkColour = async (next) => {
    const ids = [...selected];
    setBulkColourOpen(false);
    try {
      const res = await bulkProspectAction({ action: "colour", ids, colour: next });
      toast.success(res.data?.detail || "Colour applied");
      setSelected(new Set());
      loadRecords();
    } catch (e) {
      toast.error(apiError(e, "Couldn't colour those contacts"));
    }
  };

  // Bulk "Set buying preferences": stamp one preference set across every ticked
  // row. Christian Gravias, Monday DOMA 12840979019 (2026-08-19) — "200 buyers
  // looking to purchase a 1 bedroom apartment in Melbourne for a price of
  // $100k-$300k. How can we add them all in and specify those buying
  // preferences?" Writes through to each row's mirrored CRM contact, which is
  // what buyer matching and the buyer stats read.
  const runBulkPreferences = async ({ preferences, mode, includeSecond }) => {
    const ids = [...selected];
    try {
      const res = await bulkProspectAction({
        action: "preferences",
        ids,
        preferences,
        mode,
        person: includeSecond ? "both" : "primary",
      });
      toast.success(res.data?.detail || "Buying preferences applied");
      setBulkPrefsOpen(false);
      setSelected(new Set());
      loadRecords();
    } catch (e) {
      toast.error(apiError(e, "Couldn't apply those preferences"));
    }
  };

  const runBulkDelete = async () => {
    const ids = [...selected];
    // On the agency-wide grid a delete lands in the OWNING agent's database,
    // not the admin's, so name them in the confirm. An admin asked for exactly
    // this delete on 25 Aug 2026 believing every row was their own.
    const picked = new Set(ids.map(String));
    const otherOwners = [
      ...new Set(
        records
          .filter(
            (r) =>
              picked.has(String(r.id)) &&
              r.agent &&
              me?.id &&
              String(r.agent) !== String(me.id)
          )
          .map((r) => r.agent_name || "another agent")
      ),
    ].sort();
    const whose = otherOwners.length
      ? `\n\nThese rows are not in your own account: they belong to ${otherOwners.join(
          ", "
        )}. Deleting them removes them from ${
          otherOwners.length === 1 ? "that agent's" : "those agents'"
        } database too.`
      : "";
    if (!window.confirm(`Delete ${ids.length} selected contact${ids.length === 1 ? "" : "s"} permanently? This removes them, their notes and history — it can't be undone.${whose}`)) return;
    try {
      const res = await bulkProspectAction({ action: "delete", ids });
      toast.success(res.data?.detail || "Contacts deleted");
      setSelected(new Set());
      loadRecords();
      loadLists(view, agentScope);
    } catch (e) {
      toast.error(apiError(e, "Couldn't delete those contacts"));
    }
  };

  // Bulk "Convert to appraisal": push selected owner records into the Off-Market
  // Pipeline. Owners-only (buyers have no property to appraise), so the button is
  // only shown in the owners view.
  const runBulkConvert = async () => {
    const ids = [...selected];
    if (!window.confirm(`Send ${ids.length} selected propert${ids.length === 1 ? "y" : "ies"} to the Off-Market Pipeline as appraisals? They'll stay in the database, flagged as Appraisal.`)) return;
    try {
      const res = await bulkProspectAction({ action: "convert", ids });
      toast.success(res.data?.detail || "Converted to the Off-Market Pipeline");
      setSelected(new Set());
      loadRecords();
      loadLists(view, agentScope);
    } catch (e) {
      toast.error(apiError(e, "Couldn't convert those records"));
    }
  };

  // The way back from the Database to Prospects: the selected owners become
  // seller leads on the board (Christian Gravias, 20 Oct 2026: "make this a
  // button where agents can easily move the data from database back to
  // pipeline and not only pipeline to database"). The agent picks the column,
  // New / Cold / Warm / Hot / Long Term (Genneva Smarrelli, CRM-2026-00121:
  // "there is no way that I can currently move a property from database to my
  // pipeline" - the old button was a hover-only hourglass that only did Long
  // Term). A person whose seller lead was filed into the Database gets that
  // same lead back, not a second card. The rows stay here; the Database is
  // still the agent's calling list.
  const openPipelineMove = (ids, bulk = false) => {
    // Start on the rows' own temperature when they all share one.
    const temps = new Set(records.filter((r) => ids.includes(r.id)).map((r) => r.lead_temp || ""));
    const [only] = temps;
    setPipelineMove({ ids, bulk, defaultPlacement: temps.size === 1 && only ? only : "long_term" });
  };

  // Several rows, or a colleague's row seen by the principal, become the
  // appraisal on the server straight away: a form per row would be 30 forms,
  // and on a colleague's row the form would make the appraisal the principal's.
  const appraisalOpensForm = (ids) => canWrite && ids.length === 1;

  const confirmPipelineMove = async (placement) => {
    // One of the agent's own rows goes to Appraisal through the Add Appraisal
    // form, the same as the row's Convert button (Christian Gravias, Monday
    // 13343075874: "Please add Appraisal to this tile also").
    if (placement === "appraisal" && appraisalOpensForm(pipelineMove.ids)) {
      const [id] = pipelineMove.ids;
      return convertRow(records.find((r) => r.id === id) || { id });
    }
    try {
      const res = await bulkProspectAction({ action: "pipeline", placement, ids: pipelineMove.ids });
      toast.success(res.data?.detail || "Moved to the Pipeline");
      if (pipelineMove.bulk) setSelected(new Set());
      // Appraisal flags the rows here, so the grid has to show it.
      if (placement === "appraisal") {
        loadRecords();
        loadLists(view, agentScope);
      }
      return true;
    } catch (e) {
      toast.error(apiError(e, "Couldn't move those to the Pipeline"));
      return false;
    }
  };

  // Per-row quick convert (no drawer). Same as the drawer's button: opens the Add
  // Appraisal page with this property's address already filled in. The record
  // only leaves the database once that appraisal is saved.
  const convertRow = async (record) => {
    try {
      const res = await getProspectAppraisalPrefill(record.id);
      navigate("/properties/add-property", {
        state: {
          listingType: "appraisal",
          prospectRecordId: record.id,
          prefill: res.data?.prefill || {},
        },
      });
      return true;
    } catch (e) {
      toast.error(apiError(e, "Couldn't open the appraisal form"));
      return false;
    }
  };

  // "Add / move to list" via the modal: optionally create a new list, then file
  // the rows under it in the way the agent asked for.
  //
  // The modal asks because the page kept guessing wrong. It moved the rows
  // until 1 Sep 2026, which is how "3 Bed HW" silently dropped from 161 to 88;
  // it then only ever added, which is how rows filed under "Find Data" stayed
  // in the tab they came from too (Paul Jimoulis, CRM-2026-00024, 4 Sep 2026).
  // `mode` is add | move | only, and a move names the tab being left so the
  // backend can take the rows out of it.
  const confirmMoveToList = async ({ listId, newName, mode = "add", fromListId = null }) => {
    const ids = [...selected];
    try {
      let targetId = await resolveListChoice(view, listId);
      if (newName) {
        const res = await createProspectList({ view, name: newName });
        targetId = res.data.id;
      }
      const res2 = await bulkProspectAction({
        action: "add_to_list",
        ids,
        list: targetId,
        mode,
        // Only sent on a move: the tab currently open is the one to leave.
        ...(mode === "move" && fromListId ? { from_list: fromListId } : {}),
      });
      const fallback =
        mode === "move" ? `Moved ${ids.length} to the list` : `Added ${ids.length} to the list`;
      toast.success(res2?.data?.detail || fallback);
      setSelected(new Set());
      loadLists(view, agentScope);
      loadRecords();
    } catch (e) {
      toast.error(
        apiError(e, mode === "move"
          ? "Couldn't move those records"
          : "Couldn't add those records to the list")
      );
    }
  };

  // ---- lists ----
  // A list can be created on its own, or inside a category heading. The
  // category is normally just the heading its lists sit under, so "new
  // category" with a first list inside it is this same call with a group name
  // (Monday 12849996728, Christian Gravias, 2026-08-20). A category with
  // nothing in it yet has no list to be, and is handled below.
  const submitNewList = async ({ name, group_name }) => {
    const group = (group_name || "").trim();
    // A category on its own, with no sub-category in it yet: there is no list
    // to create, so the heading is saved on the view config and the row shows
    // up empty, and its sheets are added with the + under the grid
    // (Christian Gravias, 2026-08-22).
    if (!name.trim()) {
      if (!group) return;
      if (categoryTitles.some((t) => sameName(t, group))) {
        toast.error(`You already have a category called "${group}".`);
        throw new Error("duplicate category");
      }
      await saveCategories([...emptyGroups, group]);
      toast.success(`Category "${group}" created - click it, then + at the bottom of the grid to add a sheet`);
      return;
    }
    try {
      await createProspectList({ view, name, group_name });
      loadLists(view, agentScope);
      toast.success(
        group ? `"${name}" created inside ${group}` : `List "${name}" created`
      );
    } catch (e) {
      toast.error(apiError(e, "Couldn't create that list"));
      throw e;
    }
  };

  // The empty categories, saved on their own: the columns menu has its own
  // saver, and the view-config PUT is partial, so neither overwrites the other.
  const saveCategories = async (next) => {
    const before = emptyGroups;
    setEmptyGroups(next);
    try {
      await saveViewConfig({ view, empty_groups: next });
    } catch (e) {
      setEmptyGroups(before); // put the row back rather than pretend it saved
      toast.error(apiError(e, "Couldn't save that category"));
      throw e;
    }
  };

  // Clicking the heading opens the category as a list in its own right
  // (Christian Gravias, 2026-08-24: "I want agents to have the choice of using
  // the heading as a list"). The heading is backed by a real list of the same
  // name inside the category, made the first time it is clicked - so it emails,
  // texts, shares, imports and takes new records exactly like any other tab,
  // with no sub-category to create first.
  const [openingCategory, setOpeningCategory] = useState(null);
  const openCategoryList = async (group) => {
    if (group.selfList) {
      setActiveList(group.selfList.id);
      return;
    }
    // Sheets in it already: open the first one, the way a workbook opens on
    // sheet 1. Only a category with nothing in it at all still gets a list of
    // its own made for it, so a stray click can no longer leave a spare tab
    // behind (which is what "Stop using the heading as a list" was for).
    if (group.lists.length) {
      setActiveList(group.lists[0].id);
      return;
    }
    // Someone else's database: nothing of mine to create in it, and an empty
    // heading there has nothing to show.
    if (!canManage) return;
    setOpeningCategory(group.title);
    try {
      const res = await createProspectList({
        view,
        name: group.title,
        group_name: group.title,
      });
      await loadLists(view, agentScope);
      if (res?.data?.id) setActiveList(res.data.id);
      toast.success(`"${group.title}" is now a list of its own - records you add here go into it`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't open that category as a list"));
    } finally {
      setOpeningCategory(null);
    }
  };

  // Undo for a heading that was opened as a list by mistake. Only offered while
  // it holds nothing: a category with sub-lists has no chip for its own list, so
  // without this there would be no way back out of one stray click.
  const unlinkCategoryList = async (group) => {
    setTabMenu(null);
    if (!group.selfList || group.selfList.record_count) return;
    try {
      await deleteProspectList(group.selfList.id);
      if (activeList === group.selfList.id) setActiveList("all");
      await loadLists(view, agentScope);
      applyShares(null);
      toast.success(`"${group.title}" is a heading again`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't undo that"));
    }
  };

  // An empty category has no lists to delete, so it is removed from the
  // config; one with lists in it is emptied tab by tab first. A heading being
  // used as a list is deleted with it - it IS the heading, not a tab under it.
  const deleteCategory = async (group) => {
    setTabMenu(null);
    if (group.lists.length) {
      toast.error(`Delete the lists inside "${group.title}" first.`);
      return;
    }
    const count = group.selfList?.record_count || 0;
    const warn = count
      ? `Delete the category "${group.title}"? Its ${count} record${count === 1 ? "" : "s"} won't be deleted - they'll move back to "All".`
      : `Delete the empty category "${group.title}"?`;
    if (!window.confirm(warn)) return;
    if (group.selfList) {
      try {
        await deleteProspectList(group.selfList.id);
        if (activeList === group.selfList.id) setActiveList("all");
        await loadLists(view, agentScope);
        applyShares(null);
      } catch (e) {
        toast.error(apiError(e, "Couldn't delete that category"));
        return;
      }
    }
    await saveCategories(emptyGroups.filter((t) => !sameName(t, group.title)));
    toast.success(`Category "${group.title}" deleted`);
  };

  // Rename and re-home in one PATCH: moving a list between categories is the
  // same edit as renaming it, and doing both at once keeps the tab from
  // colliding with a same-named list in the category it is landing in.
  const submitEditList = async (l, { name, group_name }) => {
    if (name === l.name && group_name === (l.group_name || "")) return;
    try {
      await updateProspectList(l.id, { view, name, group_name });
      loadLists(view, agentScope);
      applyShares(null);
      toast.success(
        group_name ? `"${name}" moved into ${group_name}` : `"${name}" saved`
      );
    } catch (e) {
      toast.error(apiError(e, "Couldn't save that list"));
      throw e;
    }
  };
  const editList = (l) => {
    setTabMenu(null);
    setListForm({ mode: "edit", list: l });
  };
  // Deletes the list heading only - its records aren't deleted, they fall
  // back to "All" (see ProspectListDetailView.destroy on the backend).
  const deleteList = async (l) => {
    const count = l.record_count || 0;
    const warn = count
      ? `Delete the list "${l.name}"? Its ${count} record${count === 1 ? "" : "s"} won't be deleted - they'll move back to "All".`
      : `Delete the empty list "${l.name}"?`;
    if (!window.confirm(warn)) return;
    try {
      await deleteProspectList(l.id);
      if (activeList === l.id) setActiveList("all");
      loadLists(view, agentScope);
      toast.success(`List "${l.name}" deleted`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't delete that list"));
    }
  };

  // Combine two tabs in one step (Christian Gravias, Monday 13202407781).
  const mergeList = async (source, target) => {
    try {
      const { data } = await mergeProspectList(source.id, target.id);
      if (activeList === source.id) setActiveList(target.id);
      loadLists(view, agentScope);
      loadRecords();
      toast.success(
        `Merged "${source.name}" into "${target.name}" (${data?.record_count ?? ""} records)`.replace(" ()", "")
      );
    } catch (e) {
      toast.error(apiError(e, "Couldn't merge those lists"));
      throw e;
    }
  };

  // Lists grouped under a title, normally the suburb the upload was for:
  // "Preston" over "1 bedroom unit", "2 bedroom unit"… (Christian Gravias,
  // 2026-08-06). Lists with no group keep sitting on the top row.
  const listGroups = useMemo(() => {
    const groups = [];
    const byTitle = new Map();
    lists.forEach((l) => {
      const title = (l.group_name || "").trim();
      if (!title) return;
      if (!byTitle.has(title)) {
        const g = { title, lists: [] };
        byTitle.set(title, g);
        groups.push(g);
      }
      byTitle.get(title).lists.push(l);
    });
    // A category the agent created but hasn't put anything in yet still gets
    // its chip, so it survives a reload and has a + under the grid. Matched
    // loosely against the headings that do have lists: a CSV import can spell
    // the same suburb differently, and one category is one row either way.
    emptyGroups.forEach((raw) => {
      const title = (raw || "").trim();
      if (!title) return;
      const taken = groups.some((g) => g.title.toLowerCase() === title.toLowerCase());
      if (taken) return;
      const g = { title, lists: [] };
      byTitle.set(title, g);
      groups.push(g);
    });
    // "1 bedroom unit" then "2" then "10" — a plain sort puts 10 second.
    const lead = (t) => {
      const m = /\d+/.exec(t || "");
      return m ? Number(m[0]) : Number.MAX_SAFE_INTEGER;
    };
    groups.forEach((g) => {
      // The heading used as a list itself: the list inside the category that
      // carries the category's own name (Christian Gravias, 2026-08-24 - "I
      // should be able to click onto Test and access the list without having to
      // create another list"). It is the heading's own chip, so it is taken out
      // of the sub-list row rather than drawn twice.
      g.selfList = g.lists.find((l) => sameName(l.name, g.title)) || null;
      if (g.selfList) g.lists = g.lists.filter((l) => l !== g.selfList);
      g.lists.sort(
        (a, b) => lead(a.name) - lead(b.name) || (a.name || "").localeCompare(b.name || "")
      );
      g.lists = applySavedOrder(g.lists, listOrder, (l) => l.id);
    });
    // A-Z is only the DEFAULT now: a category the agent dragged sits where they
    // put it, and one they never touched stays alphabetical behind those
    // (Monday 12963781026).
    groups.sort((a, b) => a.title.localeCompare(b.title));
    return applySavedOrder(groups, groupOrder, (g) => g.title);
  }, [lists, emptyGroups, groupOrder, listOrder]);

  const categoryTitles = useMemo(() => listGroups.map((g) => g.title), [listGroups]);

  // Categories with no list in them yet, for the "which list?" pickers. Only
  // on my own Database: on a shared one there is nothing of mine to create.
  const emptyCategories = useMemo(
    () => (canManage ? emptyCategoryTitles(lists, emptyGroups) : []),
    [canManage, lists, emptyGroups]
  );

  const looseLists = useMemo(
    () =>
      applySavedOrder(
        lists.filter((l) => !(l.group_name || "").trim()),
        listOrder,
        (l) => l.id
      ),
    [lists, listOrder]
  );

  // ---- the workbook: a category up top, its sheets along the bottom ----
  // Christian Gravias, Monday 12908273441, 2026-08-28: "Please just make it
  // like excel. The agent can just pick a category and then can create separate
  // lists down the bottom like excel." So the category row is the workbook
  // picker and the lists inside it are drawn as sheet tabs under the grid.
  //
  // Which category is open is DERIVED from the selected list rather than kept
  // in a state of its own: the list can also be changed by the import wizard,
  // by "add to list", and by a share opening, and a second source of truth
  // would drift out of step with every one of them.
  const activeGroup = useMemo(() => {
    if (activeList === "all") return "";
    const l = lists.find((x) => String(x.id) === String(activeList));
    return (l?.group_name || "").trim();
  }, [lists, activeList]);

  const activeGroupObj = useMemo(
    () => (activeGroup ? listGroups.find((g) => sameName(g.title, activeGroup)) || null : null),
    [listGroups, activeGroup]
  );

  // The tabs under the grid: the sheets of the category that is open.
  // The heading's own list comes first, the way Excel keeps sheet 1 on the left.
  //
  // A list with NO category used to be a sheet down here under "All", which put
  // it below the whole grid and mixed its rows in with every category's under
  // "All" (Christian Gravias, Monday 13065049862, 2026-09-17: "Not sure why
  // Social media leads is being mixed in with preston. They are 2 different
  // categories... rather then social media leads sitting down the bottom of all
  // 'All' it has its own section up the top"). Those lists are chips on the top
  // row now, so they are never drawn down here.
  //
  // Under "All" the strip shows EVERY category's sheets, category by category
  // (Christian Gravias, Monday 13202768612, 2026-10-05: "If the setting is on
  // all in the database it should also show all the sheets down below"). It
  // used to say "No sheets in here yet" under All, however many there were.
  //
  // That still left an agent whose lists have NO category with an empty strip
  // under All (Christian Gravias, Monday 13202768612, 2026-10-07, on Genneva's
  // Database, all four of whose lists are uncategorised: "Just checked Gennevas
  // list. Not fixe"). So under All the strip ends with those lists too. They
  // keep their chip up top as well; the strip is the "every sheet" view, and
  // under a category it still shows only that category's sheets.
  const sheetsOf = useCallback(
    (g) => {
      if (!g) return [];
      // The heading's own sheet leads, the way Excel keeps sheet 1 on the left -
      // until the agent drags it somewhere else, which the saved order then wins.
      const all = g.selfList ? [g.selfList, ...g.lists] : g.lists;
      return applySavedOrder(all, listOrder, (l) => l.id);
    },
    [listOrder]
  );

  const activeSheets = useMemo(() => {
    if (!activeGroup) return [...listGroups.flatMap(sheetsOf), ...looseLists];
    return sheetsOf(activeGroupObj);
  }, [activeGroup, activeGroupObj, listGroups, looseLists, sheetsOf]);

  // Which category a sheet on the strip belongs to. Under "All" the strip
  // spans every category, so each tab finds its own.
  const sheetGroup = useMemo(() => {
    const m = new Map();
    listGroups.forEach((g) => sheetsOf(g).forEach((l) => m.set(String(l.id), g)));
    return m;
  }, [listGroups, sheetsOf]);

  // ---- dragging the tabs into the order the agent wants ----
  // Paul Jimoulis (Monday 12963781026, CRM-2026-00021, 2026-09-03): "the tabs at
  // the top of the screen, can you please make there moveable as they are
  // currently stuck and i want to select the order." Both strips were sorted
  // FOR the agent - the categories A-Z, the sheets by their leading number - so
  // the one they work every morning could never be put first.
  //
  // Native HTML5 drag, the same gesture and the same shape of handler the
  // column headers on this page already use, rather than another dnd library.
  const dragTab = useRef(null); // { strip: "group" | "sheet", key }
  const [dragOverTab, setDragOverTab] = useState(null);

  const endTabDrag = () => {
    dragTab.current = null;
    setDragOverTab(null);
  };

  // The strip moves under the cursor first and the save follows: a dropped tab
  // that sat still until the network came back would read as a failed drag.
  const saveTabOrder = async (next, apply) => {
    apply();
    try {
      await saveViewConfig({ view, ...next });
    } catch (e) {
      toast.error(apiError(e, "Couldn't save that tab order"));
      loadConfig(view, agentScope); // put the strip back the way the server has it
    }
  };

  const moveGroup = (from, to) => {
    if (!canManage) return;
    const next = moveInOrder(listGroups.map((g) => g.title), from, to);
    if (!next) return;
    saveTabOrder({ group_order: next }, () => setGroupOrder(next));
  };

  // `strip` is the row the tab being dragged belongs to: the sheets under the
  // grid, or - for a list with no category - the chips along the top.
  const moveSheet = (from, to, strip = activeSheets) => {
    if (!canManage) return;
    const moved = moveInOrder(strip.map((l) => l.id), from, to);
    if (!moved) return;
    // Only the sheets on screen moved. Every other category's sheets keep the
    // place they were already saved in, so ordering one workbook never
    // reshuffles another.
    const onScreen = new Set(moved.map((id) => String(id)));
    const next = [...moved, ...listOrder.filter((id) => !onScreen.has(String(id)))];
    saveTabOrder({ list_order: next }, () => setListOrder(next));
  };

  // Drag is a mouse gesture and this page is used on a phone, so the tab menu
  // carries the same move as two buttons - the reason the caret menu exists at
  // all (Christian Gravias, 2026-08-28).
  const groupIndex = (g) => listGroups.findIndex((x) => sameName(x.title, g.title));
  const sheetIndex = (l, strip = activeSheets) =>
    strip.findIndex((x) => String(x.id) === String(l.id));

  const nudgeGroup = (g, delta) => {
    const titles = listGroups.map((x) => x.title);
    const to = groupIndex(g) + delta;
    if (to < 0 || to >= titles.length) return;
    setTabMenu(null);
    moveGroup(g.title, titles[to]);
  };

  const nudgeSheet = (l, delta, strip = activeSheets) => {
    const ids = strip.map((x) => x.id);
    const to = sheetIndex(l, strip) + delta;
    if (to < 0 || to >= ids.length) return;
    setTabMenu(null);
    moveSheet(l.id, ids[to], strip);
  };

  const [addingSheet, setAddingSheet] = useState(false);
  const [renameSheet, setRenameSheet] = useState(null);

  // Excel's + at the end of the tab strip: it makes the sheet there and then
  // and drops straight into naming it on the tab, instead of asking for a name
  // in a dialog first ("We dont need list & new list Just add a button at the
  // bottom like excel"). The sheet lands in whichever category is open.
  const addSheet = async () => {
    if (!canManage || addingSheet) return;
    // With no category open the + starts a standalone list, so the names it
    // must not collide with are the chips up top, not the empty sheet strip.
    const peers = activeGroup ? activeSheets : looseLists;
    const taken = new Set(peers.map((l) => (l.name || "").trim().toLowerCase()));
    let n = peers.length + 1;
    let name = `Sheet ${n}`;
    while (taken.has(name.toLowerCase())) name = `Sheet ${++n}`;
    setAddingSheet(true);
    try {
      const res = await createProspectList({ view, name, group_name: activeGroup });
      await loadLists(view, agentScope);
      if (res?.data?.id) {
        setActiveList(res.data.id);
        setRenameSheet({ id: res.data.id, value: name });
      }
    } catch (e) {
      toast.error(apiError(e, "Couldn't add that sheet"));
    } finally {
      setAddingSheet(false);
    }
  };

  const startSheetRename = (l) => {
    setTabMenu(null);
    if (!canManage) return;
    setRenameSheet({ id: l.id, value: l.name || "" });
  };

  // `group` is passed only for the tab that IS the heading, so renaming it
  // renames the category with it rather than orphaning the tab.
  const commitSheetRename = async (l, group) => {
    const next = (renameSheet?.value || "").trim();
    setRenameSheet(null);
    if (!next || next === l.name) return;
    if (group) {
      await applyGroupRename(group, next);
      return;
    }
    await submitEditList(l, { name: next, group_name: l.group_name || "" }).catch(() => {});
  };

  // Retitle a whole group: one heading over all of its lists, so it is renamed
  // in one place rather than list by list.
  const renameGroup = async (group) => {
    setTabMenu(null);
    // Only a heading on the config is "empty" - one being used as a list has a
    // real list behind it that has to be re-titled with it.
    const empty = !group.lists.length && !group.selfList;
    const title = prompt(
      empty
        ? `Rename the category "${group.title}". Clear it to delete the category.`
        : `Rename the category "${group.title}". Clear it to drop its lists back to the top row.`,
      group.title
    );
    if (title === null) return;
    await applyGroupRename(group, title.trim());
  };

  // The rename itself, split out from the prompt: renaming the sheet that IS
  // the heading (done in the tab strip, Excel style) has to come through here
  // too, or the tab drops out of its own category and reads as an ordinary
  // sheet still carrying the old name.
  const applyGroupRename = async (group, next) => {
    const empty = !group.lists.length && !group.selfList;
    if (next === group.title) return;
    // The saved tab order is keyed on the heading, so a rename has to be
    // carried into it or the category falls out of the place the agent dragged
    // it to and comes back at the alphabetical end (Monday 12963781026).
    const carryOrder = () => {
      if (!groupOrder.some((t) => sameName(t, group.title))) return;
      const reordered = next
        ? groupOrder.map((t) => (sameName(t, group.title) ? next : t))
        : groupOrder.filter((t) => !sameName(t, group.title));
      setGroupOrder(reordered);
      saveViewConfig({ view, group_order: reordered }).catch(() => {});
    };
    // Nothing in it yet: there are no tabs to re-title, only the heading the
    // agent saved on their config.
    if (empty) {
      const rest = emptyGroups.filter((t) => !sameName(t, group.title));
      await saveCategories(next ? [...rest, next] : rest);
      carryOrder();
      toast.success(next ? `Category renamed to "${next}"` : `Category "${group.title}" deleted`);
      return;
    }
    try {
      // One call for the whole heading, not a PATCH per tab: the heading is
      // what a group share is keyed on, so renaming it tab by tab could lose
      // the share halfway through.
      await renameProspectGroup({ view, group_name: group.title, new_name: next });
      // The heading's own list carries the heading's name, so it is re-titled
      // too - otherwise renaming the category leaves it behind as a sub-tab
      // still called by the old name.
      if (group.selfList && next) {
        await updateProspectList(group.selfList.id, {
          view,
          name: next,
          group_name: next,
        }).catch(() => {});
      }
      // A category created empty and filled later is still on the config, so
      // the saved heading follows the rename instead of coming back as a
      // second, empty row under the old name.
      if (emptyGroups.some((t) => sameName(t, group.title))) {
        const rest = emptyGroups.filter((t) => !sameName(t, group.title));
        await saveCategories(next ? [...rest, next] : rest);
      }
      carryOrder();
      loadLists(view, agentScope);
      applyShares(null);
      toast.success(next ? `Category renamed to "${next}"` : "Category heading removed");
    } catch (e) {
      toast.error(apiError(e, "Couldn't rename that group"));
    }
  };

  // ---- sharing one group / one tab ----
  // Right-clicking a tab or a suburb heading opens this menu, which is where
  // "share people & remove from groups" lives for that one group.
  const openTabMenu = (e, entry) => {
    if (!canManage) return; // someone else's database: not yours to share
    e.preventDefault();
    e.stopPropagation();
    setTabMenu({ x: e.clientX, y: e.clientY, ...entry });
  };

  const shareGroup = (group) => {
    setTabMenu(null);
    setShareTarget({
      scope: "group",
      view,
      group_name: group.title,
      label: group.title,
    });
  };

  const shareList = (l) => {
    setTabMenu(null);
    setShareTarget({ scope: "list", list: l.id, label: l.name });
  };

  // ---- columns ----
  const persistConfig = async (next) => {
    const payload = {
      view,
      hidden_columns: hidden,
      column_order: order,
      column_labels: labels,
      ...next,
    };
    setHidden(payload.hidden_columns);
    setOrder(payload.column_order);
    setLabels(payload.column_labels);
    await saveViewConfig(payload).catch(() => {});
  };
  const hideColumn = (key) => {
    setMenuCol(null);
    persistConfig({ hidden_columns: [...hidden, key] });
  };
  const unhide = (key) => persistConfig({ hidden_columns: hidden.filter((k) => k !== key) });

  const promptColumn = async (afterKey) => {
    const label = prompt("New column name");
    if (!label) return;
    const type = prompt("Type: text, number, date, money, status", "text") || "text";
    try {
      const res = await createCustomColumn({ view, label, type });
      const newKey = res?.data?.key;
      await loadConfig(view, agentScope);
      // Position the new column immediately to the right of `afterKey`.
      if (afterKey && newKey) {
        const keys = allColumns.map((c) => c.key).filter((k) => k !== newKey);
        const idx = keys.indexOf(afterKey);
        keys.splice(idx >= 0 ? idx + 1 : keys.length, 0, newKey);
        persistConfig({ column_order: keys });
      }
      toast.success(`Column "${label}" added`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't add that column"));
    }
  };
  const insertColumnRight = (col) => {
    setMenuCol(null);
    promptColumn(col.key);
  };

  const renameColumn = async (col) => {
    setMenuCol(null);
    const label = prompt("Rename column", col.label);
    if (!label || label === col.label) return;
    try {
      if (col.custom) {
        await updateCustomColumn(col.columnId, { label });
        loadConfig(view, agentScope);
      } else {
        // Built-in column: store a per-agent label override.
        persistConfig({ column_labels: { ...labels, [col.key]: label } });
      }
    } catch (e) {
      toast.error(apiError(e, "Couldn't rename that column"));
    }
  };
  const removeColumn = async (col) => {
    setMenuCol(null);
    if (col.custom) {
      if (!window.confirm(`Delete column "${col.label}"? Values in it will be lost.`)) return;
      try {
        await deleteCustomColumn(col.columnId);
        loadConfig(view, agentScope);
        toast.success("Column deleted");
      } catch (e) {
        toast.error(apiError(e, "Couldn't delete that column"));
      }
    } else {
      // Built-in columns can't be destroyed — Delete hides them (no data lost).
      hideColumn(col.key);
      toast("Built-in column hidden (built-ins can't be deleted).");
    }
  };

  // Inline edit of a custom-field cell. Saves the whole merged custom_fields map.
  const saveCell = async (record, colKey, value) => {
    setEditing(null);
    const current = record.custom_fields || {};
    if ((current[colKey] || "") === value) return;
    const merged = { ...current, [colKey]: value };
    // optimistic local update
    setRecords((rows) => rows.map((r) => (r.id === record.id ? { ...r, custom_fields: merged } : r)));
    try {
      await updateProspect(record.id, { custom_fields: merged });
    } catch (e) {
      toast.error(apiError(e, "Couldn't save"));
      loadRecords();
    }
  };
  const onDrop = (key) => {
    if (!dragKey.current || dragKey.current === key) return;
    const keys = visibleColumns.map((c) => c.key);
    const from = keys.indexOf(dragKey.current);
    const to = keys.indexOf(key);
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    // Merge with hidden keys preserved at the end of the saved order.
    const full = [...keys, ...allColumns.map((c) => c.key).filter((k) => !keys.includes(k))];
    dragKey.current = null;
    persistConfig({ column_order: full });
  };


  const hiddenColObjs = allColumns.filter((c) => hidden.includes(c.key));

  return (
    <div className={embedded ? "max-w-full" : "p-6 max-w-full"}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className={embedded ? "text-lg font-extrabold text-gray-900" : "text-2xl font-extrabold text-gray-900"}>
              {isShared ? `${viewingAgent.name || "Agent"}'s Database` : "Database"}
            </h1>
            {/* Database switcher: your own, plus every database shared with you
                (Christian Gravias, Monday 12817267594 — agents work in teams).
                It appears once somebody has actually shared with you, and for
                nobody else: a database is private to its owner whatever your
                role (George Bottas, CRM-2026-00029). Plain admins used to get it
                unconditionally because they had the whole agency to pick from;
                they no longer do. The super admin and the dev accounts get every
                agent listed here by role, read-only (CRM-2026-00073). */}
            {sharedWithMe.length > 0 && (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setSwitcherOpen((o) => !o)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-bold text-gray-700 flex items-center gap-1.5"
                >
                  <Users size={13} style={{ color: TEAL }} />
                  {isShared ? viewingAgent.name || "Shared" : "My Database"}
                  <ChevronDown size={13} className="text-gray-400" />
                </button>
                {switcherOpen && (
                  <div className="absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-40 py-1">
                    <button
                      onClick={() => openDatabase(null)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${!isShared ? "font-bold text-[#0e7d86]" : "text-gray-700"}`}
                    >
                      My Database
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400 font-bold">
                      {reviewAccess
                        ? isPrincipalView
                          ? "Every agent · view only"
                          : "All databases · view only"
                        : "Shared with me"}
                    </p>
                    {sharedWithMe.map((sh) => {
                      const who = sh.owner_detail?.name || sh.owner_detail?.email || "Agent";
                      return (
                        <button
                          key={sh.id || `share-${sh.owner}`}
                          onClick={() => openDatabase(sh.owner, who, sh.can_edit)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                            String(agentScope) === String(sh.owner) ? "font-bold text-[#0e7d86]" : "text-gray-700"
                          }`}
                        >
                          <span className="truncate">{who}</span>
                          <span className="text-[10px] text-gray-400 shrink-0">
                            {sh.record_count} {sh.can_edit ? "· edit" : "· view"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {isShared && !canWrite && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold">
                <Eye size={12} /> View only
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {isShared
              ? `${
                  viaReview
                    ? isPrincipalView
                      ? `${
                          viewingAgent.name || "This agent"
                        }'s database, open to you as the agency's super admin.`
                      : `${
                          viewingAgent.name || "This agent"
                        }'s database, open to you for testing and support.`
                    : partialTargets.length
                    ? `${partialTargets.join(", ")} — shared with you by ${
                        viewingAgent.name || "another agent"
                      }.`
                    : `Shared with you by ${viewingAgent.name || "another agent"}.`
                } ${
                  canWrite
                    ? "You can call, note and book call-backs on these records."
                    : "Read-only: you can look up these records but not change them."
                }`
              : "Your contact and property database. Call, qualify, set callbacks, convert to appraisals."}
          </p>
        </div>
        {/* Data actions grouped together */}
        <div className="flex items-center gap-1 border border-gray-200 rounded-xl p-1 bg-white">
          {/* Uploading, deleting an upload and reshaping lists/columns belong to
              whoever owns the database, so they come off the page entirely while
              a colleague's database is open — matching what the API allows. */}
          {canManage && (
            <>
              <button onClick={() => setShowImport(true)} className="px-3 py-2 rounded-lg hover:bg-gray-50 text-xs font-bold flex items-center gap-1.5">
                <Upload size={14} /> Upload CSV
              </button>
              <button onClick={() => setShowDelete(true)} className="px-3 py-2 rounded-lg hover:bg-rose-50 text-rose-600 text-xs font-bold flex items-center gap-1.5">
                <Trash2 size={14} /> Delete upload
              </button>
              <button
                onClick={() => setShowShare(true)}
                title="Invite other agents to your Database"
                className="px-3 py-2 rounded-lg hover:bg-gray-50 text-xs font-bold flex items-center gap-1.5 text-[#0e7d86]"
              >
                <Users size={14} /> Share
              </button>
            </>
          )}
          {canWrite && (
            <button onClick={() => setShowAdd(true)} className="px-3 py-2 rounded-lg text-white text-xs font-bold flex items-center gap-1.5" style={{ background: TEAL }}>
              <Plus size={14} /> Add record
            </button>
          )}
          {isShared && viaReview && isPrincipalView && (
            <button
              onClick={() => setShowTransfer(true)}
              title="Move this agent's whole Database to another agent"
              className="px-3 py-2 rounded-lg hover:bg-gray-50 text-xs font-bold flex items-center gap-1.5 text-[#0e7d86]"
            >
              <ArrowRightLeft size={14} /> Transfer
            </button>
          )}
          {!canManage && (
            <button
              onClick={() => setShowShare(true)}
              title="Manage who can see your Database"
              className="px-3 py-2 rounded-lg hover:bg-gray-50 text-xs font-bold flex items-center gap-1.5 text-[#0e7d86]"
            >
              <Users size={14} /> Sharing
            </button>
          )}
        </div>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-3.5 mb-3.5">
        <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
          {["owners", "buyers"].map((v) => (
            <button
              key={v}
              onClick={() => switchView(v)}
              className={`px-5 py-1.5 rounded-md text-sm font-bold capitalize ${view === v ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"}`}
            >
              {v}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 font-semibold">
          {loading ? "…" : `${records.length} ${view}`}
        </span>
        {/* Green = spoken to, call-back booked. The list opens hottest first, so a
            call-back sits at the bottom of its own temperature, not of the list. */}
        {!loading && callbackCount > 0 && (
          <span
            title="Green rows have been called and have a call-back booked. The list opens Hot, then Warm, then Cold; within each, the call-backs come after the rows still to be called, soonest first."
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#2f9e63]"
          >
            {/* Same green as the rows themselves, so the legend matches the grid. */}
            <span className="w-2.5 h-2.5 rounded-full bg-[#c3ebd3] border border-[#34b56a]" />
            {callbackCount} call-back{callbackCount === 1 ? "" : "s"} booked
          </span>
        )}
      </div>

      {/* The categories, along the top. Their lists are NOT drawn here any
          more: they are sheet tabs along the bottom of the grid, the way Excel
          does it (Christian Gravias, Monday 12908273441, 2026-08-28 - "The
          agent can just pick a category and then can create separate lists down
          the bottom like excel"). Picking a category up here swaps the sheet
          bar underneath; "All" is the workbook of lists that sit under no
          heading, which is where a plain CSV upload lands. */}
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Chip active={activeList === "all"} onClick={() => setActiveList("all")}>All</Chip>
          {loading && !lists.length && (
            <Loader2 size={15} className="animate-spin text-[#55c5d0]" />
          )}
          {/* A list that sits in no category is its own section up here,
              beside the categories, instead of a sheet tab buried under the
              bottom of the grid (Christian Gravias, Monday 13065049862,
              2026-09-17: "rather then social media leads sitting down the
              bottom of all All it has its own section up the top and
              Preston/Social media leads are seperate"). It is a list, so it
              carries the list's own menu - rename, move into a category,
              share, delete - not the category one. */}
          {looseLists.map((l) => {
            const on = String(activeList) === String(l.id);
            const share = listShareCounts.get(l.id);
            const over =
              dragOverTab?.strip === "loose" && String(dragOverTab.key) === String(l.id);
            // Renaming puts an <input> in the chip, and a draggable parent eats
            // the click-and-drag that selects text inside it.
            const canDrag = canManage && renameSheet?.id !== l.id;
            const entry = { type: "list", list: l, group: null, loose: true };
            return (
              <span
                key={l.id}
                draggable={canDrag}
                onDragStart={() => (dragTab.current = { strip: "loose", key: l.id })}
                onDragOver={(e) => {
                  if (dragTab.current?.strip !== "loose") return;
                  e.preventDefault();
                  if (String(dragTab.current.key) !== String(l.id)) {
                    setDragOverTab({ strip: "loose", key: l.id });
                  }
                }}
                onDragLeave={() =>
                  setDragOverTab((d) => (d && String(d.key) === String(l.id) ? null : d))
                }
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragTab.current;
                  endTabDrag();
                  if (from?.strip === "loose") moveSheet(from.key, l.id, looseLists);
                }}
                onDragEnd={endTabDrag}
                className={`shrink-0 flex items-center rounded-lg ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${over ? "ring-2 ring-[#0e7d86] ring-offset-1" : ""} ${on ? "bg-[#55c5d0] text-white" : "bg-[#eaf7f8] text-[#0e7d86]"}`}
              >
                {canManage && (
                  <GripVertical
                    size={12}
                    className="ml-1 -mr-0.5 opacity-40 shrink-0"
                    aria-hidden="true"
                  />
                )}
                {renameSheet?.id === l.id ? (
                  <input
                    autoFocus
                    value={renameSheet.value}
                    onChange={(e) => setRenameSheet({ id: l.id, value: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    onBlur={() => commitSheetRename(l, null)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") setRenameSheet(null);
                    }}
                    className="w-32 mx-1.5 my-1 px-1.5 py-0.5 text-xs font-bold text-[#0e7d86] border border-[#55c5d0] rounded outline-none"
                  />
                ) : (
                  <button
                    onClick={() => setActiveList(l.id)}
                    onDoubleClick={canManage ? () => startSheetRename(l) : undefined}
                    onContextMenu={(e) => openTabMenu(e, entry)}
                    title={
                      canManage
                        ? `${l.name} - a list of its own, in no category. Double-click to rename it, drag it to move it along the row; right-click to share it or file it under a category.`
                        : l.name
                    }
                    className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-extrabold uppercase tracking-wide"
                  >
                    <Layers size={12} /> {l.name}
                    <span className="font-bold normal-case opacity-60">{l.record_count}</span>
                  </button>
                )}
                {share && (
                  <button
                    onClick={() => shareList(l)}
                    title={
                      share.everyone
                        ? "Shared with everyone at Collings"
                        : `Shared with ${share.agents} agent${share.agents === 1 ? "" : "s"}`
                    }
                    className="pl-0.5 pr-1 py-1.5 flex items-center gap-0.5 text-[10px] font-bold"
                  >
                    {share.everyone ? <Globe size={11} /> : <Users size={11} />}
                    {!share.everyone && share.agents}
                  </button>
                )}
                {canManage && (
                  <button
                    onClick={(e) => openTabMenu(e, entry)}
                    title={`Options for ${l.name}`}
                    className="pr-1.5 pl-0.5 py-1.5 opacity-50 hover:opacity-100"
                  >
                    <ChevronDown size={12} />
                  </button>
                )}
              </span>
            );
          })}
          {listGroups.map((g) => {
            // The number on a category stays what's in the whole of it - its
            // own records plus everything on its sheets - so the agent keeps
            // the total they scan by.
            const total =
              (g.selfList?.record_count || 0) +
              g.lists.reduce((n, l) => n + (l.record_count || 0), 0);
            const gShare = groupShareCounts.get(g.title);
            const open = sameName(activeGroup, g.title);
            const busy = openingCategory === g.title;
            // Dragged over by another category: show where it would land.
            const over =
              dragOverTab?.strip === "group" && sameName(dragOverTab.key, g.title);
            return (
              <span
                key={g.title}
                // Drag to reorder (Monday 12963781026). Only on your own
                // Database: the order is saved against whoever is logged in, so
                // dragging a colleague's tabs would save nothing you could see.
                draggable={canManage}
                onDragStart={() => (dragTab.current = { strip: "group", key: g.title })}
                onDragOver={(e) => {
                  if (dragTab.current?.strip !== "group") return;
                  e.preventDefault();
                  if (!sameName(dragTab.current.key, g.title)) {
                    setDragOverTab({ strip: "group", key: g.title });
                  }
                }}
                onDragLeave={() => setDragOverTab((d) => (d && sameName(d.key, g.title) ? null : d))}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragTab.current;
                  endTabDrag();
                  if (from?.strip === "group") moveGroup(from.key, g.title);
                }}
                onDragEnd={endTabDrag}
                className={`shrink-0 flex items-center rounded-lg ${canManage ? "cursor-grab active:cursor-grabbing" : ""} ${over ? "ring-2 ring-[#0e7d86] ring-offset-1" : ""} ${open ? "bg-[#55c5d0] text-white" : "bg-[#eaf7f8] text-[#0e7d86]"}`}
              >
                {/* The handle says out loud that the chip moves - "they are
                    currently stuck" was the complaint, and a chip that happens
                    to be draggable looks exactly like one that isn't. */}
                {canManage && (
                  <GripVertical
                    size={12}
                    className="ml-1 -mr-0.5 opacity-40 shrink-0"
                    aria-hidden="true"
                  />
                )}
                <button
                  onClick={() => openCategoryList(g)}
                  disabled={busy}
                  onContextMenu={(e) => openTabMenu(e, { type: "group", group: g })}
                  title={
                    canManage
                      ? `Open ${g.title} - its sheets are along the bottom of the grid. Drag it to move it along the row; right-click to share it with other agents.`
                      : g.title
                  }
                  className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-extrabold uppercase tracking-wide"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}{" "}
                  {g.title}
                  <span className="font-bold normal-case opacity-60">{total}</span>
                </button>
                {canManage && (
                  <button
                    onClick={() => renameGroup(g)}
                    title={`Rename the category ${g.title}`}
                    className="pl-0.5 py-1.5 opacity-60 hover:opacity-100"
                  >
                    <Pencil size={11} />
                  </button>
                )}
                {/* No sheets under it, so there is no tab to delete it by: the
                    category carries its own x, the way a chip does. */}
                {canManage && !g.lists.length && (
                  <button
                    onClick={() => deleteCategory(g)}
                    title={`Delete the category ${g.title}`}
                    className="pr-2 pl-0.5 py-1.5 text-[11px] font-bold opacity-60 hover:opacity-100"
                  >
                    <X size={12} />
                  </button>
                )}
                {/* Shared with someone: the badge says who, and opens the same
                    popover the right-click menu does. */}
                {canManage && gShare && (
                  <button
                    onClick={() => shareGroup(g)}
                    title={
                      gShare.everyone
                        ? "Shared with everyone at Collings"
                        : `Shared with ${gShare.agents} agent${gShare.agents === 1 ? "" : "s"}`
                    }
                    className="pr-2 pl-0.5 py-1.5 flex items-center gap-0.5 text-[10px] font-bold"
                  >
                    {gShare.everyone ? <Globe size={12} /> : <Users size={12} />}
                    {!gShare.everyone && gShare.agents}
                  </button>
                )}
              </span>
            );
          })}
          {/* A category is a heading of its own, with or without sheets in it
              yet, e.g. "Preston" over "1br 43 High" (Monday 12849996728). The
              sheets themselves are added with the + at the bottom, so this is
              the only add button left up here. */}
          {canManage && (
            <button
              onClick={() => setListForm({ mode: "category", defaultGroup: "" })}
              title="A heading of its own, e.g. Preston - add its sheets with the + at the bottom of the grid"
              className="px-3 py-1.5 rounded-full border border-dashed border-[#55c5d0] text-[#0e7d86] text-xs font-bold"
            >
              + New category
            </button>
          )}
          {/* Every list is also an audience in the Communications Hub, so an agent
              can email or text a database they just built (Christian Gravias,
              2026-08-03). */}
          {activeList !== "all" && (
            <>
              <button
                onClick={() => navigate(`/communications/campaigns/new?prospect_list=${activeList}&channel=email`)}
                title="Start an email campaign to everyone on this list"
                className="px-3 py-1.5 rounded-full border border-[#55c5d0] text-[#0e7d86] bg-[#eaf7f8] text-xs font-bold flex items-center gap-1.5"
              >
                <Mail size={12} /> Email this list
              </button>
              <button
                onClick={() => navigate(`/communications/campaigns/new?prospect_list=${activeList}&channel=sms`)}
                title="Start an SMS campaign to everyone on this list"
                className="px-3 py-1.5 rounded-full border border-[#55c5d0] text-[#0e7d86] bg-[#eaf7f8] text-xs font-bold flex items-center gap-1.5"
              >
                <MessageSquare size={12} /> Text this list
              </button>
            </>
          )}
          <button
            onClick={() => setShowConverted((s) => !s)}
            className={`ml-auto px-3 py-1.5 rounded-full border text-xs font-bold flex items-center gap-1.5 ${showConverted ? "bg-[#efeafd] border-[#cbbcf5] text-[#5b3fc0]" : "border-gray-200 text-gray-500"}`}
            title="Show only the rows already marked as appraisals"
          >
            <RotateCcw size={12} /> Appraisals only
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 border border-gray-200 bg-white rounded-lg px-3 py-2">
          <Search size={15} className="text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, address, suburb, mobile…"
            className="flex-1 outline-none text-sm"
          />
        </div>
        {view === "owners" && (
          <select value={suburb} onChange={(e) => setSuburb(e.target.value)} className="border border-gray-200 rounded-lg px-2.5 py-2 text-xs font-semibold">
            <option value="">Suburb: any</option>
            {suburbs.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
        <select value={beds} onChange={(e) => setBeds(e.target.value)} className="border border-gray-200 rounded-lg px-2.5 py-2 text-xs font-semibold">
          <option value="">Beds: any</option>
          {[1, 2, 3, 4, 5].map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
        <select value={temp} onChange={(e) => setTemp(e.target.value)} className="border border-gray-200 rounded-lg px-2.5 py-2 text-xs font-semibold">
          <option value="">Temp: any</option>
          <option value="cold">Cold</option>
          <option value="warm">Warm</option>
          <option value="hot">Hot</option>
        </select>
        {/* Work one colour at a time: "show me just the red ones". */}
        <select
          value={colour}
          onChange={(e) => setColour(e.target.value)}
          title="Show only the contacts you've coloured this colour"
          className="border-2 rounded-lg px-2.5 py-1.5 text-xs font-bold"
          style={
            colourOf(colour)
              ? { borderColor: colourOf(colour).dot, color: colourOf(colour).dot }
              : { borderColor: "var(--c-line-brand-strong)", color: "var(--c-on-brand)" }
          }
        >
          <option value="">🎨 Colour: any</option>
          {COLOURS.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
          <option value="none">No colour yet</option>
        </select>
        {/* Work a calling list with the opted-out people out of the way, or audit
            who has unsubscribed. */}
        <select
          value={unsubFilter}
          onChange={(e) => setUnsubFilter(e.target.value)}
          title="Do not contact"
          className={`border-2 rounded-lg px-2.5 py-1.5 text-xs font-bold ${
            unsubFilter ? "border-rose-400 text-rose-600" : "border-gray-200 text-gray-600"
          }`}
        >
          <option value="">🚫 Do not contact: any</option>
          <option value="1">Only unsubscribed</option>
          <option value="0">Hide unsubscribed</option>
        </select>
        {/* Owners Homer shows for sale now or withdrawn: the ones worth a call. */}
        {view === "owners" && (
          <select
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            title="On the market, from Homer"
            className={`border-2 rounded-lg px-2.5 py-1.5 text-xs font-bold ${
              market ? "border-emerald-400 text-emerald-700" : "border-gray-200 text-gray-600"
            }`}
          >
            {MARKET_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>{o.value ? o.label : "🏷️ " + o.label}</option>
            ))}
          </select>
        )}
        <button
          onClick={() => setCallbacksDue((c) => !c)}
          className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 ${callbacksDue ? "text-white" : "border border-gray-200 text-gray-600"}`}
          style={callbacksDue ? { background: TEAL } : {}}
        >
          <Clock size={13} /> Callbacks due
        </button>
        <div className="ml-auto inline-flex border border-gray-200 rounded-lg overflow-hidden">
          <button onClick={() => setMode("grid")} className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 ${mode === "grid" ? "text-white" : "text-gray-600"}`} style={mode === "grid" ? { background: TEAL } : {}}>
            <LayoutGrid size={13} /> Grid
          </button>
          <button onClick={() => setMode("map")} className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 ${mode === "map" ? "text-white" : "text-gray-600"}`} style={mode === "map" ? { background: TEAL } : {}}>
            <MapPin size={13} /> Map
          </button>
        </div>
      </div>

      {/* Grid or Map */}
      {mode === "map" ? (
        <ProspectMap onOpenRecord={setDrawerId} agent={agentScope} />
      ) : (
        <>
          <div
            ref={scrollRef}
            className="bg-white border border-gray-200 rounded-t-xl shadow-sm overflow-auto relative min-h-[160px] max-h-[calc(100vh-300px)]"
          >
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-30">
                <div className="flex items-center gap-2 text-gray-500 text-sm font-semibold">
                  <Loader2 size={18} className="animate-spin text-[#55c5d0]" />
                  Loading {view}…
                </div>
              </div>
            )}
            {/* Once the columns have been frozen the table stops sizing itself
                from its content, which is what made every hover and every
                scroll re-measure the whole grid. The widths are the ones the
                browser had already chosen, so nothing moves on screen. */}
            <table
              ref={tableRef}
              className="w-full border-collapse min-w-[980px]"
              style={
                grid.colWidths
                  ? { tableLayout: "fixed", width: grid.tableWidth }
                  : undefined
              }
            >
              {grid.colWidths && (
                <colgroup>
                  {grid.colWidths.map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
              )}
              <thead>
                <tr>
                  <th className="w-9 text-center sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                    {/* Nothing to bulk-action on a view-only shared database, so the
                        tick boxes come off rather than leading to a dead end. */}
                    {canSelect && (
                      <input type="checkbox" checked={records.length > 0 && selected.size === records.length} onChange={toggleAll} />
                    )}
                  </th>
                  {/* Colour code, labelled like every other column so it's findable. */}
                  <th
                    title="Colour code your contacts"
                    className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10 px-2 py-2.5 text-[11.5px] uppercase tracking-wide text-gray-500 font-extrabold whitespace-nowrap"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Palette size={13} className="text-[#55c5d0]" /> Colour
                    </span>
                  </th>
                  {visibleColumns.map((c) => (
                    <th
                      key={c.key}
                      draggable
                      onDragStart={() => (dragKey.current = c.key)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(c.key)}
                      className="text-left sticky top-0 bg-gray-50 border-b border-gray-200 z-10 px-3 py-2.5 text-[11.5px] uppercase tracking-wide text-gray-500 font-extrabold whitespace-nowrap cursor-grab group"
                    >
                      <span className="inline-flex items-center gap-1 relative">
                        {c.label}
                        {c.custom && <span className="text-[#55c5d0] normal-case font-bold">·</span>}
                        {/* Sort arrow on every column: A-Z, Z-A, then back to the
                            default calling order. Its own button rather than the
                            whole header, so dragging a column to reorder it and
                            sorting by it stay two different gestures. */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSort(c.key);
                          }}
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                          title={
                            sortBy?.key !== c.key
                              ? `Sort by ${c.label}`
                              : sortBy.dir === "asc"
                              ? "Sorted A-Z. Click for Z-A."
                              : "Sorted Z-A. Click to clear."
                          }
                          className={
                            sortBy?.key === c.key
                              ? "text-[#55c5d0]"
                              : "text-gray-300 hover:text-gray-500"
                          }
                        >
                          {sortBy?.key !== c.key ? (
                            <ArrowUpDown size={12} />
                          ) : sortBy.dir === "asc" ? (
                            <ArrowUp size={12} />
                          ) : (
                            <ArrowDown size={12} />
                          )}
                        </button>
                        {canManage && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuCol(menuCol === c.key ? null : c.key);
                            }}
                            title="Column options"
                            className="opacity-0 group-hover:opacity-100 text-gray-400"
                          >
                            <MoreVertical size={13} />
                          </button>
                        )}
                        {menuCol === c.key && (
                          <div className="absolute top-6 left-0 z-20 flex flex-col whitespace-normal bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-44 normal-case font-semibold text-gray-700">
                            <MenuItem onClick={() => renameColumn(c)}>Rename</MenuItem>
                            <MenuItem onClick={() => insertColumnRight(c)}>Insert column right</MenuItem>
                            <MenuItem onClick={() => hideColumn(c.key)}>Hide</MenuItem>
                            <MenuItem danger onClick={() => removeColumn(c)}>Delete</MenuItem>
                          </div>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Stands in for the rows above the fold, so the scrollbar and
                    the scroll position describe the whole database. */}
                {grid.padTop > 0 && (
                  <tr aria-hidden="true" style={{ height: grid.padTop }}>
                    <td colSpan={gridColCount} className="p-0 border-0" />
                  </tr>
                )}
                {drawnRecords.map((r) => {
                  // A colour the agent picked paints the WHOLE line and outranks
                  // everything else (Christian, 2026-08-10). It is a deliberate act,
                  // the same as colouring a row in their spreadsheet, so nothing may
                  // paint over it. Ticking the row for a bulk action only deepens the
                  // same colour, so the colour code never disappears mid-job. Rows with
                  // no colour keep the permanent green when a call-back is booked.
                  const rowColour = colourOf(r.colour);
                  const worked = hasCallback(r);
                  const isSelected = selected.has(r.id);
                  const unsubbed = isUnsubbed(r);
                  const appraisal = isAppraisal(r);
                  // The line carries the agent's colour, so the left edge is free to
                  // carry the other signals: rose for do-not-contact first, because
                  // it is the one an agent must not miss before dialling, then green
                  // when a call-back is booked.
                  // Violet marks a row that is already an appraisal.
                  const edge = unsubbed ? UNSUB_EDGE : appraisal ? APPRAISAL_EDGE : worked ? GREEN_EDGE : rowColour ? rowColour.dot : null;
                  const rowBg = rowColour
                    ? isSelected
                      ? rowColour.rowSelCls
                      : rowColour.rowCls
                    : appraisal
                    ? isSelected
                      ? APPRAISAL_ROW_SELECTED
                      : APPRAISAL_ROW
                    : worked
                    ? isSelected
                      ? GREEN_ROW_SELECTED
                      : GREEN_ROW
                    : isSelected
                    ? "bg-[#eef9fa] hover:bg-[#eef9fa]"
                    : "hover:bg-[#f6fcfd]";
                  return (
                  <tr
                    key={r.id}
                    data-row
                    style={grid.ready ? { height: grid.rowH } : undefined}
                    onClick={() => setDrawerId(r.id)}
                    title={
                      unsubbed
                        ? `Do not contact: ${unsubbedNames(r).join(", ")}. Excluded from every email and SMS.`
                        : appraisal
                        ? "Marked as an appraisal. It's on the Off-Market Pipeline."
                        : worked
                        ? `Called, call-back booked for ${r.next_call_at}`
                        : undefined
                    }
                    className={`group cursor-pointer ${rowBg}`}
                  >
                    <td className="w-9 text-center relative" onClick={(e) => e.stopPropagation()}>
                      {edge && (
                        <span
                          className="absolute left-0 top-0 bottom-0 w-[4px]"
                          style={{ background: edge }}
                        />
                      )}
                      {canSelect && (
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRow(r.id)} />
                      )}
                    </td>
                    <td className="relative px-2" onClick={(e) => e.stopPropagation()}>
                      {/* Always visible, and a rainbow dot when empty so it reads as
                          "pick a colour" at a glance rather than hiding until hover. */}
                      {canWrite ? (
                      <button
                        onClick={() => setColourRow(colourRow === r.id ? null : r.id)}
                        title={rowColour ? `Colour: ${rowColour.label}. Click to change.` : "Colour code this contact"}
                        className={`w-6 h-6 rounded-full border-2 border-white ring-1 flex items-center justify-center shadow-sm hover:scale-110 transition ${
                          rowColour
                            ? "ring-black/15"
                            : "ring-gray-300 bg-gradient-to-br from-[#e5484d] via-[#e3b427] to-[#2f9bd6]"
                        }`}
                        style={rowColour ? { background: rowColour.dot } : undefined}
                      >
                        {!rowColour && <Palette size={12} className="text-white drop-shadow" />}
                      </button>
                      ) : (
                        /* Read-only: the owner's colour still reads at a glance, but
                           the "pick a colour" rainbow dot would invite a click that
                           the API refuses. */
                        rowColour ? (
                          <span
                            title={`Colour: ${rowColour.label}`}
                            className="w-6 h-6 rounded-full border-2 border-white ring-1 ring-black/15 inline-block shadow-sm"
                            style={{ background: rowColour.dot }}
                          />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )
                      )}
                      {colourRow === r.id && (
                        <ColourPicker
                          value={r.colour || ""}
                          onPick={(next) => setRowColour(r, next)}
                          onClose={() => setColourRow(null)}
                        />
                      )}
                    </td>
                    {visibleColumns.map((c) => {
                      const isEditing = c.custom && editing?.recordId === r.id && editing?.colKey === c.key;
                      return (
                        <td
                          key={c.key}
                          onClick={c.custom && canWrite ? (e) => {
                            e.stopPropagation();
                            setEditing({ recordId: r.id, colKey: c.key });
                          } : undefined}
                          className={`border-b border-gray-100 px-3 py-2.5 text-[12.8px] text-gray-700 whitespace-nowrap ${c.note ? "" : "max-w-[240px]"} overflow-hidden text-ellipsis ${c.custom && canWrite ? "cursor-text" : ""}`}
                        >
                          {isEditing ? (
                            <input
                              autoFocus
                              defaultValue={r.custom_fields?.[c.key] || ""}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => saveCell(r, c.key, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.target.blur();
                                if (e.key === "Escape") setEditing(null);
                              }}
                              className="w-full border border-[#55c5d0] rounded px-1.5 py-0.5 text-[12.8px] outline-none"
                            />
                          ) : appraisal && c.key === "address" ? (
                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                              {renderCell(r, c)}
                              <AppraisalPill
                                onOpen={r.converted_property
                                  ? () => navigate(`/properties/${r.converted_property}`)
                                  : undefined}
                              />
                            </span>
                          ) : (
                            renderCell(r, c)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
                {grid.padBottom > 0 && (
                  <tr aria-hidden="true" style={{ height: grid.padBottom }}>
                    <td colSpan={gridColCount} className="p-0 border-0" />
                  </tr>
                )}
                {!records.length && !loading && (
                  <tr>
                    <td colSpan={gridColCount} className="text-center text-gray-400 py-10">
                      {isShared
                        ? "No records here. Try clearing a filter or another list."
                        : "No records. Clear a filter, or upload a CSV to this list."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Sheet tabs, exactly where Excel puts them - along the bottom of
              the grid, with one + on the end (Christian Gravias, Monday
              12908273441, 2026-08-28, screenshot of a workbook's tab strip and
              its "Add Sheet" button). The sheets are the lists inside the
              category picked above, or under "All" every category's sheets
              and then the lists with no category (Monday 13202768612). */}
          <div className="flex items-stretch gap-0.5 bg-gray-50 border border-gray-200 border-t-0 rounded-b-xl px-1.5 overflow-x-auto">
            {activeSheets.map((l) => {
              // The tab that IS the category heading. Renaming it renames the
              // category, and its menu is the category's menu.
              const tabGroup = activeGroupObj || sheetGroup.get(String(l.id)) || null;
              const isSelf = !!tabGroup && l === tabGroup.selfList;
              // A list with no category (only ever here under "All") carries
              // the same LIST menu its chip up top does.
              const isLoose = !tabGroup;
              const on = String(activeList) === String(l.id);
              const share = listShareCounts.get(l.id);
              // The heading's own tab opens the CATEGORY's menu (rename here
              // renames the category), but it is still a tab on the sheet
              // strip - so it carries the sheet with it, or "Move left" would
              // move the chip up top instead of the tab you right-clicked.
              const entry = isSelf
                ? { type: "group", group: tabGroup, sheet: l }
                : { type: "list", list: l, group: tabGroup, loose: isLoose };
              const over =
                dragOverTab?.strip === "sheet" && String(dragOverTab.key) === String(l.id);
              // Renaming puts an <input> in the tab, and a draggable parent
              // eats the click-and-drag that selects text inside it.
              // Under "All" the strip runs across categories, and a drop there
              // would shuffle one workbook's sheets against another's - so the
              // drag lives inside a category (the tab menu still moves it).
              const canDrag = canManage && !!activeGroup && renameSheet?.id !== l.id;
              return (
                <span
                  key={l.id}
                  // Excel lets you drag a sheet tab along the strip, and this
                  // page is meant to BE Excel (Monday 12908273441); Paul
                  // Jimoulis asked for the same on the tabs above (12963781026).
                  draggable={canDrag}
                  onDragStart={() => (dragTab.current = { strip: "sheet", key: l.id })}
                  onDragOver={(e) => {
                    if (dragTab.current?.strip !== "sheet") return;
                    e.preventDefault();
                    if (String(dragTab.current.key) !== String(l.id)) {
                      setDragOverTab({ strip: "sheet", key: l.id });
                    }
                  }}
                  onDragLeave={() =>
                    setDragOverTab((d) => (d && String(d.key) === String(l.id) ? null : d))
                  }
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragTab.current;
                    endTabDrag();
                    if (from?.strip === "sheet") moveSheet(from.key, l.id);
                  }}
                  onDragEnd={endTabDrag}
                  className={`shrink-0 flex items-center border-t-2 ${canDrag ? "cursor-grab active:cursor-grabbing" : ""} ${over ? "bg-white ring-2 ring-inset ring-[#0e7d86]" : ""} ${on ? "bg-white border-t-[#55c5d0] text-[#0e7d86] shadow-sm" : "border-t-transparent text-gray-500 hover:bg-white/70"}`}
                >
                  {/* A list with no category renames in its chip up top, or
                      two autofocused boxes would fight over the one draft. */}
                  {renameSheet?.id === l.id && !isLoose ? (
                    <input
                      autoFocus
                      value={renameSheet.value}
                      onChange={(e) => setRenameSheet({ id: l.id, value: e.target.value })}
                      onFocus={(e) => e.target.select()}
                      onBlur={() => commitSheetRename(l, isSelf ? tabGroup : null)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        // Escape leaves the name alone; the blur that follows
                        // finds no draft left to save.
                        if (e.key === "Escape") setRenameSheet(null);
                      }}
                      className="w-32 mx-1.5 my-1 px-1.5 py-0.5 text-xs font-bold border border-[#55c5d0] rounded outline-none"
                    />
                  ) : (
                    <button
                      onClick={() => setActiveList(l.id)}
                      onDoubleClick={canManage ? () => startSheetRename(l) : undefined}
                      onContextMenu={(e) => openTabMenu(e, entry)}
                      title={
                        isSelf
                          ? `${l.name} - the category's own sheet. Double-click to rename it, drag it to move it along the strip.`
                          : canManage
                            ? `${l.name} - double-click to rename it, drag it to move it along the strip`
                            : l.name
                      }
                      className={`px-3 py-2 text-xs whitespace-nowrap ${on ? "font-extrabold" : "font-bold"}`}
                    >
                      {!activeGroup && !isSelf && tabGroup && (
                        <span className="font-semibold opacity-50">{tabGroup.title} · </span>
                      )}
                      {l.name} <span className="opacity-60">{l.record_count}</span>
                    </button>
                  )}
                  {share && (
                    <button
                      onClick={() => shareList(l)}
                      title={
                        share.everyone
                          ? "Shared with everyone at Collings"
                          : `Shared with ${share.agents} agent${share.agents === 1 ? "" : "s"}`
                      }
                      className="pl-0.5 pr-1 py-2 flex items-center gap-0.5 text-[10px] font-bold text-[#0e7d86]"
                    >
                      {share.everyone ? <Globe size={11} /> : <Users size={11} />}
                      {!share.everyone && share.agents}
                    </button>
                  )}
                  {/* The caret Excel and Sheets put on a tab: rename, move,
                      share and delete without a right-click, which is the only
                      way in on a phone. */}
                  {canManage && (
                    <button
                      onClick={(e) => openTabMenu(e, entry)}
                      title={`Options for ${l.name}`}
                      className="pr-1.5 pl-0.5 py-2 opacity-50 hover:opacity-100"
                    >
                      <ChevronDown size={12} />
                    </button>
                  )}
                </span>
              );
            })}
            {!activeSheets.length && (
              <span className="px-2 py-2.5 text-[11px] text-gray-400 font-semibold">
                {canManage
                  ? activeGroup
                    ? `No sheets in ${activeGroup} yet - press + to add one`
                    : "No lists yet - press + to add one"
                  : "No sheets in here yet"}
              </span>
            )}
            {canManage && (
              <button
                onClick={addSheet}
                disabled={addingSheet}
                title="Add Sheet"
                className="shrink-0 my-1 px-2.5 rounded text-gray-500 hover:bg-white hover:text-[#0e7d86] flex items-center disabled:opacity-50"
              >
                {addingSheet ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Plus size={15} />
                )}
              </button>
            )}
          </div>

          {/* Hidden columns bar */}
          {canManage && hiddenColObjs.length > 0 && (
            <div className="flex items-center gap-2 mt-2.5 text-xs text-gray-500 flex-wrap">
              Hidden:
              {hiddenColObjs.map((c) => (
                <button key={c.key} onClick={() => unhide(c.key)} className="px-2.5 py-1 rounded-full border border-gray-200 text-[11.5px] font-bold">
                  {c.label} ✕
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 bg-gray-900 text-white rounded-xl px-4 py-2.5 flex items-center gap-2.5 shadow-2xl z-30">
          <b className="text-sm">{selected.size} selected</b>
          {/* Emailable count of the selection. Prospecting rows are mostly
              phone-only, so the Email send reaches fewer than the row count —
              spelling it out here stops the "4313 records but 1398 emailed?"
              confusion (Caleb, CRM-2026-00053). */}
          {(() => {
            const withEmail = records.filter((r) => selected.has(r.id) && allEmails(r).length > 0).length;
            return canWrite && withEmail < selected.size ? (
              <span className="text-[11px] text-gray-300" title="Only records with an email address can be emailed; the rest are phone-only.">
                · {withEmail} with an email
              </span>
            ) : null;
          })()}
          {/* The super admin on an agent's read-only Database gets Move to
              Pipeline only (CRM-2026-00121); the rest change the rows. */}
          {canWrite && (
            <>
              <div className="relative">
                <button
                  onClick={() => setBulkColourOpen((o) => !o)}
                  title="Colour code all the selected contacts"
                  className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
                >
                  <Palette size={13} className="text-[#55c5d0]" />
                  Colour
                </button>
                {bulkColourOpen && (
                  <ColourPicker
                    value=""
                    drop="up"
                    onPick={runBulkColour}
                    onClose={() => setBulkColourOpen(false)}
                  />
                )}
              </div>
              <button
                onClick={() => runBulkSend("email")}
                disabled={!!bulkSending}
                title="Open the campaign composer with these contacts as the audience"
                className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
              >
                <Mail size={13} className="text-[#55c5d0]" />
                {bulkSending === "email" ? "Opening…" : "Email"}
              </button>
              <button
                onClick={() => runBulkSend("sms")}
                disabled={!!bulkSending}
                title="Open the SMS composer with these contacts as the audience"
                className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-60"
              >
                <MessageSquare size={13} className="text-[#55c5d0]" />
                {bulkSending === "sms" ? "Opening…" : "SMS"}
              </button>
              {/* Sits right beside Email and SMS on purpose: this is the button that
                  takes people OUT of what those two send to. */}
              <button
                onClick={() => runBulkUnsubscribe(!selectionAllUnsubbed)}
                title={
                  selectionAllUnsubbed
                    ? "Allow contact with the selected rows again"
                    : "Mark the selected rows Do not contact — excluded from every email and SMS"
                }
                className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
              >
                <Ban size={13} className="text-rose-400" />
                {selectionAllUnsubbed ? "Allow contact" : "Do not contact"}
              </button>
              {/* Says both words on purpose: the dialog behind it does both, and
                  the old "+ Add to list" label was read as a move. */}
              <button
                onClick={() => setShowMoveList(true)}
                title="File the selected rows under another list - adding them to it, or moving them out of this tab"
                className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold"
              >
                + Add / move
              </button>
              <button onClick={() => runBulk("reminder")} className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold">⏰ Reminder</button>
              <button
                onClick={() => setBulkPrefsOpen(true)}
                title="Set the same buying preferences on every selected contact"
                className="bg-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
              >
                <SlidersHorizontal size={13} className="text-[#55c5d0]" /> Preferences
              </button>
              {view === "owners" && !showConverted && (
                <button onClick={runBulkConvert} className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5" style={{ background: TEAL }}>
                  <ArrowRightLeft size={13} /> Add as Appraisal
                </button>
              )}
            </>
          )}
          {view === "owners" && !showConverted && (
            <button
              onClick={() => openPipelineMove([...selected], true)}
              title="Put the selected owners on your Pipeline on Prospects: New, Cold, Warm, Hot, Long Term or Appraisal. They stay in the database too."
              className="bg-[#0e7d86] px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
            >
              <KanbanSquare size={13} /> Move to Pipeline
            </button>
          )}
          {canWrite && (
            <button onClick={runBulkDelete} className="bg-rose-600 hover:bg-rose-700 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5">
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button onClick={() => setSelected(new Set())} className="text-gray-400 text-xs">Clear</button>
        </div>
      )}

      {drawerId && (
        <ProspectDrawer
          key={`${drawerId}-${drawerRefresh}`}
          recordId={drawerId}
          view={view}
          onClose={() => setDrawerId(null)}
          onChanged={loadRecords}
          onEdit={(rec) => setEditRecord(rec)}
          onMoveToPipeline={canMoveToPipeline ? (rec) => openPipelineMove([rec.id]) : undefined}
          readOnly={!canWrite}
        />
      )}
      {showImport && (
        <ImportWizard
          view={view}
          lists={lists}
          // Every heading that already exists, so an import can be dropped into
          // one instead of always making a new group (Monday 12908273441).
          groups={listGroups.map((g) => g.title)}
          activeList={activeList === "all" ? "" : activeList}
          onClose={() => setShowImport(false)}
          onDone={() => {
            loadRecords();
            loadLists(view, agentScope);
          }}
        />
      )}
      {(showAdd || editRecord) && (
        <AddRecordModal
          view={view}
          columns={visibleColumns}
          activeList={activeList}
          lists={lists}
          emptyCategories={emptyCategories}
          record={editRecord}
          agent={agentScope}
          onClose={() => {
            setShowAdd(false);
            setEditRecord(null);
          }}
          onCreated={() => {
            loadRecords();
            loadLists(view, agentScope);
            if (editRecord) setDrawerRefresh((n) => n + 1); // refresh open drawer
          }}
        />
      )}
      {pipelineMove && (
        <MoveToPipelineModal
          count={pipelineMove.ids.length}
          agentName={isShared ? viewingAgent?.name : null}
          defaultPlacement={pipelineMove.defaultPlacement}
          appraisalOpensForm={appraisalOpensForm(pipelineMove.ids)}
          onClose={() => setPipelineMove(null)}
          onConfirm={confirmPipelineMove}
        />
      )}
      {showMoveList && (
        <MoveToListModal
          count={selected.size}
          lists={lists}
          emptyCategories={emptyCategories}
          allowCreate={canManage}
          allowOnly={canManage}
          // The tab on screen is what a "move" moves the rows out of. On "All"
          // there is no such tab, so the modal offers adding only.
          sourceList={activeList === "all" ? null : lists.find((l) => String(l.id) === String(activeList)) || null}
          onClose={() => setShowMoveList(false)}
          onConfirm={confirmMoveToList}
        />
      )}
      {bulkPrefsOpen && selected.size > 0 && (
        <BulkBuyerPreferencesModal
          count={selected.size}
          view={view}
          onClose={() => setBulkPrefsOpen(false)}
          onApply={runBulkPreferences}
        />
      )}
      {showShare && (
        <ShareDatabaseModal
          currentAgentId={agentScope}
          onClose={() => setShowShare(false)}
          onChanged={applyShares}
          onOpenDatabase={(id, name, canEdit) => openDatabase(id, name, canEdit)}
          onOpenTarget={(t) => {
            setShowShare(false);
            setShareTarget(t);
          }}
        />
      )}
      {showTransfer && viewingAgent && (
        <TransferDatabaseModal
          fromAgent={viewingAgent}
          onClose={() => setShowTransfer(false)}
          onDone={(target) => {
            setShowTransfer(false);
            getDatabaseShares().then((res) => applyShares(res.data)).catch(() => {});
            // Land on the Database that now holds the rows.
            if (target.isMe) openDatabase(null);
            else openDatabase(target.id, target.name || target.email, false);
          }}
        />
      )}
      {/* One group or one tab, shared with named agents or with the whole
          office (Christian Gravias, 2026-08-18). */}
      {shareTarget && (
        <ShareDatabaseModal
          target={shareTarget}
          onClose={() => setShareTarget(null)}
          onChanged={applyShares}
        />
      )}
      {tabMenu && (
        <div
          className="fixed z-[90] w-56 bg-white border border-gray-200 rounded-xl shadow-xl py-1"
          style={{ top: Math.min(tabMenu.y, window.innerHeight - 240), left: Math.min(tabMenu.x, window.innerWidth - 240) }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-bold truncate">
            {tabMenu.type === "group" ? tabMenu.group.title : tabMenu.list.name}
          </p>
          {/* The same move as the drag, for a phone: dragging is a mouse
              gesture, and the caret menu is the only way into a tab on touch
              (Monday 12963781026 asked for the order; 12908273441 is why the
              menu is here). The ends of a strip disable their own direction. */}
          {canManage && (() => {
            // A sheet tab moves along the strip under the grid; a category chip
            // moves along the row up top.
            const sheet = tabMenu.type === "list" ? tabMenu.list : tabMenu.sheet;
            // A list with no category is a chip on the top row, so Move
            // left/right walks it along that row, not along the sheet strip.
            // Under "All" a sheet moves within its own category, not along
            // the whole strip.
            const sheetStrip = tabMenu.loose
              ? looseLists
              : activeGroup
                ? activeSheets
                : sheetsOf(tabMenu.group);
            const strip = sheet ? sheetStrip : listGroups;
            const at = sheet ? sheetIndex(sheet, sheetStrip) : groupIndex(tabMenu.group);
            const nudge = (delta) =>
              sheet ? nudgeSheet(sheet, delta, sheetStrip) : nudgeGroup(tabMenu.group, delta);
            if (at < 0 || strip.length < 2) return null;
            return (
              <div className="flex items-center gap-1 px-2 pb-1.5 pt-0.5 border-b border-gray-100">
                <button
                  onClick={() => nudge(-1)}
                  disabled={at === 0}
                  title="Move this tab one place earlier"
                  className="flex-1 px-2 py-1.5 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent flex items-center justify-center gap-1"
                >
                  <ArrowLeft size={13} /> Move left
                </button>
                <button
                  onClick={() => nudge(1)}
                  disabled={at === strip.length - 1}
                  title="Move this tab one place later"
                  className="flex-1 px-2 py-1.5 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent flex items-center justify-center gap-1"
                >
                  Move right <ArrowRight size={13} />
                </button>
              </div>
            );
          })()}
          <button
            onClick={() =>
              tabMenu.type === "group" ? shareGroup(tabMenu.group) : shareList(tabMenu.list)
            }
            className="w-full text-left px-3 py-2 text-sm font-semibold text-[#0e7d86] hover:bg-gray-50 flex items-center gap-2"
          >
            <Share2 size={14} /> Share with agents…
          </button>
          <button
            onClick={() => {
              setTabMenu(null);
              tabMenu.type === "group" ? renameGroup(tabMenu.group) : startSheetRename(tabMenu.list);
            }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <Pencil size={14} />{" "}
            {tabMenu.type === "group"
              ? "Rename category"
              : tabMenu.loose
                ? "Rename list"
                : "Rename sheet"}
          </button>
          {tabMenu.type === "list" && (
            <>
              {/* Renaming happens on the tab now, so the dialog is what's left
                  for moving a sheet into another category. */}
              <button
                onClick={() => editList(tabMenu.list)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowRightLeft size={14} /> Rename or move list…
              </button>
              <button
                onClick={() => {
                  setTabMenu(null);
                  setMergeFrom(tabMenu.list);
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Layers size={14} /> Merge into another list…
              </button>
              <button
                onClick={() => {
                  setTabMenu(null);
                  deleteList(tabMenu.list);
                }}
                className="w-full text-left px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
              >
                <Trash2 size={14} /> {tabMenu.loose ? "Delete list" : "Delete sheet"}
              </button>
            </>
          )}
          {/* Opened as a list by a stray click and still empty: the way back.
              A category with no sub-lists has the x on its heading instead. */}
          {tabMenu.type === "group" &&
            !!tabMenu.group.lists.length &&
            tabMenu.group.selfList &&
            !tabMenu.group.selfList.record_count && (
              <button
                onClick={() => unlinkCategoryList(tabMenu.group)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <RotateCcw size={14} /> Stop using the heading as a list
              </button>
            )}
          {tabMenu.type === "group" && !tabMenu.group.lists.length && (
            <button
              onClick={() => deleteCategory(tabMenu.group)}
              className="w-full text-left px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
            >
              <Trash2 size={14} /> Delete category
            </button>
          )}
        </div>
      )}
      {mergeFrom && (
        <MergeListModal
          source={mergeFrom}
          lists={lists}
          onClose={() => setMergeFrom(null)}
          onConfirm={(target) => mergeList(mergeFrom, target)}
        />
      )}
      {listForm && (
        <ListFormModal
          mode={listForm.mode}
          list={listForm.list}
          categories={listGroups.map((g) => g.title)}
          defaultGroup={listForm.defaultGroup || ""}
          onClose={() => setListForm(null)}
          onSubmit={(payload) =>
            listForm.mode === "edit"
              ? submitEditList(listForm.list, payload)
              : submitNewList(payload)
          }
        />
      )}
      {showDelete && (
        <DeleteImportModal
          initialView={view}
          onClose={() => setShowDelete(false)}
          onDone={() => {
            loadRecords();
            loadLists(view, agentScope);
          }}
        />
      )}
    </div>
  );
}

// The flag on a Database row that is already an appraisal. Clicking it opens the
// appraisal itself, when the property still exists.
function AppraisalPill({ onOpen }) {
  const cls = "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#7c5cdb] text-white text-[10px] font-extrabold uppercase tracking-wide";
  if (!onOpen) {
    return <span title="Marked as an appraisal" className={cls}><ArrowRightLeft size={10} /> Appraisal</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      title="Marked as an appraisal. Click to open it."
      className={`${cls} hover:bg-[#6845d0]`}
    >
      <ArrowRightLeft size={10} /> Appraisal
    </button>
  );
}

function renderCell(r, c) {
  const v = c.get ? c.get(r) : r[c.key];
  // A name column for a person who has asked not to be contacted. The pill goes
  // next to THAT name rather than on the row, so on a jointly-owned property the
  // agent can see it is the second owner who is off limits and not both of them.
  if (c.person !== undefined && (r.people?.[c.person]?.unsubscribed)) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <span className={c.bold ? "font-bold text-gray-500 line-through" : "text-gray-500 line-through"}>
          {v || "—"}
        </span>
        <span
          title="Do not contact. Excluded from every email and SMS."
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-extrabold uppercase tracking-wide"
        >
          <Ban size={10} /> Do not contact
        </span>
      </span>
    );
  }
  // A person's name → clickable to their contact profile (where a reminder
  // series is logged), CRM-2026-00073. Resolves by email when the row isn't a
  // CRM contact yet. Falls through to plain text when there's no email to resolve.
  if (c.person !== undefined && v) {
    const p = r.people?.[c.person] || {};
    const email = p.email || (c.person === 0 ? r.email : undefined);
    if (email) {
      return (
        <ContactLink
          identity={{ email, name: v, phone: p.mobile }}
          className={`text-left hover:underline ${c.bold ? "font-bold text-[#2f9099]" : "text-[#3a99a3]"} hover:text-[#55C5D0]`}
          returnTo="/database"
          returnLabel="Database"
        >
          {v}
        </ContactLink>
      );
    }
  }
  if (c.note) {
    if (!v || !v.body) return <span className="text-gray-300">—</span>;
    return <NotePreview note={v} />;
  }
  if (c.temp) {
    return v ? (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-extrabold ${TEMP_STYLES[v] || ""}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        {v}
      </span>
    ) : null;
  }
  if (c.market && r.owner_profile?.market_status) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <MarketStatusBadge op={r.owner_profile} />
        {v ? <span>{v}</span> : null}
      </span>
    );
  }
  if (c.money) return money(v);
  // Booked call-back: green while it's still in the future (this row is done for
  // now), amber once the day arrives so it stands out as "ring them today".
  if (c.due && v)
    return (
      <span className={`font-extrabold ${callbackDue(r) ? "text-[#e8923a]" : "text-[#2f9e63]"}`}>{v}</span>
    );
  if (v === null || v === undefined || v === "") return <span className="text-gray-300">—</span>;
  return c.bold ? <b className="text-gray-900">{v}</b> : v;
}

const MenuItem = ({ children, danger, onClick }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${danger ? "text-rose-600" : "text-gray-700"}`}
  >
    {children}
  </button>
);

// `share` is { agents, everyone } when this tab has been shared on its own,
// and `onContextMenu` is the right-click that opens the share menu for it.
const Chip = ({ active, children, onDelete, onContextMenu, share, onShare, ...props }) => (
  <span
    onContextMenu={onContextMenu}
    className={`inline-flex items-center rounded-full border text-xs font-bold ${active ? "bg-[#eaf7f8] border-[#55c5d0] text-[#0e7d86]" : "border-gray-200 text-gray-600 bg-white"}`}
  >
    <button {...props} className="px-3 py-1.5">
      {children}
    </button>
    {share && (
      <button
        onClick={(e) => { e.stopPropagation(); onShare?.(); }}
        title={
          share.everyone
            ? "Shared with everyone at Collings"
            : `Shared with ${share.agents} agent${share.agents === 1 ? "" : "s"}`
        }
        className="pl-0.5 py-1.5 pr-1 flex items-center gap-0.5 text-[10px] text-[#0e7d86]"
      >
        {share.everyone ? <Globe size={11} /> : <Users size={11} />}
        {!share.everyone && share.agents}
      </button>
    )}
    {onDelete && (
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title="Delete this list"
        className="pr-2 pl-0.5 py-1.5 text-gray-400 hover:text-rose-600"
      >
        <Trash2 size={11} />
      </button>
    )}
  </span>
);
